import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import DismissibleIntro from "@/components/DismissibleIntro";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import ApeUploader from "@/components/ApeUploader";
import CopyField from "@/components/CopyField";
import PublishNowDialog from "@/components/PublishNowDialog";
import PortalCompare from "@/components/PortalCompare";
import FeedUrlPanel from "@/components/FeedUrlPanel";
import RetouchPanel from "@/components/property/RetouchPanel";
import SocialStudioTab from "@/components/SocialStudioTab";
import TaccuinoWaitingOverlay from "@/components/TaccuinoWaitingOverlay";
import MatchPanel from "@/components/MatchPanel";
import PublishMatchModal from "@/components/PublishMatchModal";
import PropertyEditModal from "@/components/PropertyEditModal";
import PhotoReviewModal from "@/components/PhotoReviewModal";
import KitProgressBanner from "@/components/KitProgressBanner";
import RemotePhotosBanner from "@/components/RemotePhotosBanner";
import DeletePropertyReasonModal from "@/components/DeletePropertyReasonModal";
import ReopenGuidesBtn from "@/components/ReopenGuidesBtn";
import PropertyVoiceFab from "@/components/PropertyVoiceFab";  // iter v29 r081 · mic persistente
import LowCreditsModal from "@/components/photo/LowCreditsModal";  // iter v33 r30
import BatchProgressOverlay from "@/components/photo/BatchProgressOverlay";  // iter v33 r35
import { useBatchEnhanceTracker } from "@/context/BatchEnhanceTracker";  // iter r95d · B1
import useEnhanceCredits, { invalidateEnhanceCreditsCache } from "@/hooks/useEnhanceCredits";  // iter v33 r30
import useTabBadges from "@/hooks/useTabBadges";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  formatPrice, authedFileUrl, PhotoImg, downloadFile, FURNISH_STYLES, resolveFurnishStyle,
  PhotoUploadProgress,
} from "@/components/propertyDetail/helpers";
import { PropertyDetailCtx } from "./propertyDetail/context";
import FotoTab from "./propertyDetail/tabs/FotoTab";
import AutopilotTab from "./propertyDetail/tabs/AutopilotTab";
import PiantaTab from "./propertyDetail/tabs/PiantaTab";
import DescrizioneTab from "./propertyDetail/tabs/DescrizioneTab";
import AnteprimaTab from "./propertyDetail/tabs/AnteprimaTab";
import DocumentiTab from "./propertyDetail/tabs/DocumentiTab";
import MaterialiTab from "./propertyDetail/tabs/MaterialiTab";
import DimmiTab from "./propertyDetail/tabs/DimmiTab";
import PortaliTab from "./propertyDetail/tabs/PortaliTab";
import ErrorBoundary from "@/components/ErrorBoundary";
import LeadTab from "./propertyDetail/tabs/LeadTab";
import CondivisioneTab from "./propertyDetail/tabs/CondivisioneTab";
import { compressPhoto } from "@/lib/compressPhoto";
import { isLikelyFloorPlan } from "@/lib/floorPlanDetector";
import { guardAIAction } from "@/hooks/useKitInProgress";
import { api, API, apiError } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { smartDownloadBlob, isIOSDevice } from "@/lib/staticExport";
import { toast } from "sonner";
import Cookies from "js-cookie";
import { sharePdf } from "@/lib/share";
import {
  ArrowLeft, UploadSimple, Sparkle, Trash, DownloadSimple, Image as ImageIcon,
  FileText, ArrowsLeftRight, Spinner, Archive, Export, ArrowSquareOut,
  ShareNetwork, QrCode, Eye, Users, Copy, Check, CurrencyEur, House,
  Microphone, Stop, FilePdf, ArrowRight, ChartBar, Armchair, Rocket, Sliders,
  ArrowsClockwise, Share,
} from "@phosphor-icons/react";

export default function PropertyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fromTaccuino = searchParams.get("from") === "taccuino";
  // iter v33 r52 · Utente entra in un annuncio appena creato dal taccuino:
  // mostra un overlay bloccante finche' tutte le foto non sono visibili
  // nella galleria. Si sblocca automaticamente quando le foto smettono
  // di aumentare (o quando raggiungono expected_photo_count). L'utente ci
  // ha chiesto esplicitamente: "una volta creato l'annuncio uno non
  // possa entrare finche' non vede tutte le fotografie sotto".
  const [waitingForPhotos, setWaitingForPhotos] = useState(fromTaccuino);
  const [property, setProperty] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  // iter v33 r18 · User M1503. Errore non-fatale (500/timeout): niente
  // toast rosso, mostro UI inline con Riprova. Solo 404 forza il redirect.
  const [loadError, setLoadError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // iter 249: { current, total, phase, pct }
  const [enhancingId, setEnhancingId] = useState(null);
  const [furnishingId, setFurnishingId] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null); // {done, total, errors}
  const [activePhotoId, setActivePhotoId] = useState(null);
  const [activePlanId, setActivePlanId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [tone, setTone] = useState("professionale");
  const [length, setLength] = useState("media");
  const [planStyle, setPlanStyle] = useState("moderno");
  // AI enhancement: locked to safe defaults (bilanciata + naturale + completo).
  // Removed agent-facing options because "solo colori"/"solo luce"/etc. were
  // confusing and produced unpredictable results. The default prompt has the
  // strictest furniture/colour preservation rules.
  // iter 256: ripristiniamo SOLO la scelta di MODALITÀ ("auto" / "custom" / "fedele")
  // — l'agente la sceglie a livello annuncio e si applica a tutte le foto
  // (sia singolo enhance che batch). Intensity/style restano fissi.
  const [enhanceMode, setEnhanceMode] = useState("auto"); // auto | custom | fedele
  const [enhanceFoci, setEnhanceFoci] = useState([]); // ["luce","colori",...] solo in "custom"
  // iter v33 r107 · User: segmented compatto Completo | Solo luce dentro
  // il tab Migliora. Default "completo" (comportamento invariato).
  // "solo_luce" mappa a focus=keep_objects backend, che preserva scena e
  // arredi ma applica la stessa qualità fotografica pro di Completo.
  const [enhanceFocus, setEnhanceFocus] = useState("completo"); // completo | solo_luce
  const [stagingId, setStagingId] = useState(null);
  const [stageStyle, setStageStyle] = useState("moderno");
  const [stageRoom, setStageRoom] = useState("");  // "" = lascia decidere all'AI
  const [restyleId, setRestyleId] = useState(null);
  const [restyleStyle, setRestyleStyle] = useState("moderno");
  const [restyleRoom, setRestyleRoom] = useState("");
  const [restyleChangeFloor, setRestyleChangeFloor] = useState(false);
  const [restyleFloorStyle, setRestyleFloorStyle] = useState("parquet_chiaro");
  const [showRestyle, setShowRestyle] = useState(false);
  // iter v28 r390 — Svuota stanza (empty_room) anche in-property.
  const [emptyRoomId, setEmptyRoomId] = useState(null);
  const [emptyRoomHint, setEmptyRoomHint] = useState("");
  const [showStage, setShowStage] = useState(false);
  const [styleConsistency, setStyleConsistency] = useState(null); // { enhance?, stage?, restyle? }
  const initialTab = searchParams.get("tab") || "foto";
  const [tab, setTabState] = useState(initialTab); // foto | autopilot | pianta | descrizione | portali | lead | condivisione | social
  // iter 312 r4 — sincronizza il tab nell'URL così il refresh ti lascia
  // dove stavi (prima tornava sempre a "foto").
  const setTab = useCallback((newTab) => {
    setTabState(newTab);
    setSearchParams((prev) => {
      const np = new URLSearchParams(prev);
      np.set("tab", newTab);
      return np;
    }, { replace: true });
  }, [setSearchParams]);

  // iter v28 r299 — Badge "Nuovo" sui tab con aggiornamenti recenti.
  // Hook esegue fetch + diff con localStorage; segna come letto il tab corrente.
  const { badges: tabBadges } = useTabBadges(id, tab);
  const [portalLinks, setPortalLinks] = useState(null);
  const [listing, setListing] = useState(null);
  const [stats, setStats] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [pack, setPack] = useState(null);
  const [packLoading, setPackLoading] = useState(false);
  const [viewings, setViewings] = useState([]);
  // iter 275 round 52: leads "solo email" (lasciati sulla landing senza prenotare visita).
  const [leads, setLeads] = useState([]);
  const [autopilot, setAutopilot] = useState(null);
  const [autopilotLoading, setAutopilotLoading] = useState(false);
  const [voiceRecording, setVoiceRecording] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [voiceUploading, setVoiceUploading] = useState(false);
  const mediaRecRef = useRef(null);
  const recChunksRef = useRef([]);
  const [sellerMode, setSellerMode] = useState(false);
  const [autopilotShowMore, setAutopilotShowMore] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  // iter 244: modal "perché elimini?" per popolare il Wrapped annuale
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  // iter r95d · B1 · leggo lo stato dei batch attivi per bloccare
  // ottimisticamente il delete se ci sono foto ancora in elaborazione.
  const { activeBatches } = useBatchEnhanceTracker() || {};
  // Publish-match modal: opens automatically right after a NEW listing
  // is created (just_created=true from /listing endpoint).
  const [matchModalOpen, setMatchModalOpen] = useState(false);
  const [matchModalShareLink, setMatchModalShareLink] = useState("");

  // iter v28 r306 — Photo Review checkpoint
  // Quando il kit auto-enhance ha finito, l'agente DEVE rivedere le foto
  // prima che i contenuti social vengano generati. Lo modal si apre
  // automaticamente con ?review=1 (link da push) o quando status=pending.
  const [photoReviewOpen, setPhotoReviewOpen] = useState(false);
  const [photoReviewStatus, setPhotoReviewStatus] = useState(null); // "pending" | "approved" | null
  const fileInputRef = useRef(null);
  const planInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    // iter v33 r4 — User M1474: «se esco dall'app e rientro mi esce sempre
    // Immobile non trovato». Race condition: dopo la creazione di un annuncio
    // dal sopralluogo, GET /properties/{id} può ritornare 404 per qualche
    // secondo finché il record non è committed. Faccio retry esponenziale
    // breve (3 tentativi a 1.5s + 3s + 5s) PRIMA di mostrare l'errore al
    // utente — risolve sia il "background → rientro" sia il "appena creato".
    const tryLoad = async (attempt = 0) => {
      try {
        const [{ data: p }, { data: ph }] = await Promise.all([
          api.get(`/properties/${id}`),
          api.get(`/properties/${id}/photos`),
        ]);
        return { p, ph };
      } catch (e) {
        const status = e?.response?.status;
        // 404 transitorio: retry. 401/403: niente retry, no permessi.
        if (status === 404 && attempt < 3) {
          const delays = [1500, 3000, 5000];
          await new Promise((r) => setTimeout(r, delays[attempt]));
          return tryLoad(attempt + 1);
        }
        throw e;
      }
    };
    try {
      const { p, ph } = await tryLoad();
      setProperty(p);
      setPhotos(ph);
      const firstPhoto = ph.find((x) => (x.photo_kind || "photo") === "photo");
      const firstPlan = ph.find((x) => x.photo_kind === "floor_plan");
      if (firstPhoto && !activePhotoId) setActivePhotoId(firstPhoto.id);
      if (firstPlan && !activePlanId) setActivePlanId(firstPlan.id);
      // Photo-style consistency: pre-fill stage / restyle pickers with the
      // last style applied on this property so the whole gallery stays
      // visually coherent. Silent (no toast).
      try {
        const [{ data: cons }, { data: preset }] = await Promise.all([
          api.get(`/properties/${id}/photo-consistency`),
          api.get(`/me/photo-style-defaults`).catch(() => ({ data: {} })),
        ]);
        setStyleConsistency(cons || null);
        // Preferenze dell'annuncio vincono sempre. Se mancano, fallback al
        // preset agente. Se mancano pure quelle, restano i default built-in.
        const eStyle = cons?.enhance?.style || preset?.style;
        const eIntensity = cons?.enhance?.intensity || preset?.intensity;
        const eFocus = cons?.enhance?.focus || preset?.focus;
        if (eStyle || eIntensity || eFocus) {
          // Sintetizzo un "effective enhance" per riflesso nella UI
          setStyleConsistency((c) => ({
            ...(c || {}),
            enhance: {
              intensity: eIntensity,
              style: eStyle,
              focus: eFocus,
              from: cons?.enhance ? "property" : "agent_preset",
            },
          }));
        }
        if (cons?.stage?.style) setStageStyle(cons.stage.style);
        if (cons?.restyle?.style) setRestyleStyle(cons.restyle.style);
        if (cons?.restyle?.change_floor != null) setRestyleChangeFloor(!!cons.restyle.change_floor);
        if (cons?.restyle?.floor_style) setRestyleFloorStyle(cons.restyle.floor_style);
      } catch { /* nessuna preferenza salvata, ok */ }
    } catch (e) {
      // iter v33 r4 — Sopra il toast: se l'utente è arrivato qui dal banner
      // "Annuncio pronto" e ancora dà 404, probabile che lo abbia eliminato
      // l'agente da un altro device. In ogni caso: niente loop di errori,
      // torna alla lista.
      //
      // iter v33 r18 · User M1503 «continui errori rossi fanno scendere la
      // gente». Nuovo comportamento:
      //   - 404 → toast + torna alla lista (annuncio davvero eliminato)
      //   - 401 → redirect implicito lato interceptor api.js
      //   - Altro (500, timeout, network) → NIENTE TOAST ROSSO, NIENTE navigate.
      //     Setto `loadError` per mostrare l'UI inline con "Riprova"/"Torna
      //     alla lista" già presente per il caso "loading appeso >12s".
      //     L'utente resta sulla pagina, controlla lui quando riprovare.
      const status = e?.response?.status;
      if (status === 404) {
        toast.error("Annuncio non trovato", {
          description: "Forse è stato eliminato o non hai ancora i permessi. Torno alla lista.",
        });
        nav("/dashboard/annunci");
      } else {
        // Silenzioso: nessun toast rosso. La UI di loading mostrerà
        // già i pulsanti Riprova/Torna alla lista dopo 12s.
        setLoadError(true);
      }
    } finally { setLoading(false); }
  }, [id, nav, activePhotoId, activePlanId]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // iter v33 r30 · Resume Batch Async — all'apertura del PropertyDetail
  // controllo se c'e' gia' un job di "Migliora tutte" in corso sul server.
  // Se si', ripristino la barra progresso (batchLoading + batchProgress)
  // e ricomincio il polling. Cosi' se l'agente ha chiuso l'app e riapre,
  // vede subito che il worker sta ancora andando.
  useEffect(() => {
    let cancelled = false;
    let poller = null;
    // iter r95d · B2 · AbortController: al unmount o cambio id
    // abortisce le fetch in flight cosi' non completano su un componente
    // gia' smontato (evita warning setState + risorse tenute aperte
    // dalla WebView iOS anche dopo naviga-away).
    const controller = new AbortController();
    (async () => {
      try {
        const { data } = await api.get(`/properties/${id}/photos/enhance-batch-active`, { signal: controller.signal });
        if (cancelled || !data?.active || !data?.job_id) return;
        setBatchLoading(true);
        setBatchProgress({
          done: data.done || 0,
          total: data.total || 0,
          errors: data.errors || 0,
          in_progress: data.in_progress || 0,
        });
        setActiveBatchJobId(data.job_id);
        // iter v33 r36 · Niente piu' toast.info persistente qui: la
        // notifica di stato la gestisce la <BatchTrayPill /> globale +
        // <BatchProgressOverlay /> quando l'agente e' in PropertyDetail.
        // r95j (feb 2026) · D.6 · retry counter: prima un singolo 502 dal
        // proxy congelava la progress bar per sempre. Ora tolleriamo fino
        // a 3 errori consecutivi prima di staccare il poller.
        let consecutiveErrs = 0;
        poller = setInterval(async () => {
          try {
            const { data: s } = await api.get(`/photos/enhance-batch-status/${data.job_id}`, { signal: controller.signal });
            if (cancelled) return;
            consecutiveErrs = 0;
            setBatchProgress({
              done: s.done || 0,
              total: s.total || 0,
              errors: s.errors || 0,
              in_progress: s.in_progress || 0,
            });
            if (s.status !== "running") {
              clearInterval(poller);
              poller = null;
              invalidateEnhanceCreditsCache();
              // Non emettiamo toast qui: BatchCompletionToaster globale
              // riceve il "closed" dal tracker e mostra un toast unico.
              setBatchLoading(false);
              setBatchProgress(null);
              setActiveBatchJobId(null);
              setCancellingBatch(false);
              load();
            }
          } catch {
            consecutiveErrs += 1;
            if (consecutiveErrs >= 3) {
              clearInterval(poller);
              poller = null;
            }
          }
        }, 3000);
      } catch { /* nessun job attivo, ok */ }
    })();
    return () => {
      cancelled = true;
      if (poller) clearInterval(poller);
      controller.abort();
    };
  }, [id, load]);

  // iter v33 r17 · User M1498 «Sto aprendo l'annuncio... rimane bianco».
  // ROOT CAUSE possibile: iOS mette in pausa i timer JS quando l'app va
  // in background; una fetch/axios timeout che dovrebbe scattare a 60s
  // non parte mai → l'utente resta nella schermata skeleton per sempre.
  // Fix: se `loading` è true da >12s, mostro pulsanti "Riprova" e "Lista
  // annunci" così l'agente non è mai bloccato. Inoltre riprovo il load
  // automaticamente quando l'app torna visibile (visibility change).
  const [loadingElapsed, setLoadingElapsed] = useState(0);
  useEffect(() => {
    if (!loading) { setLoadingElapsed(0); return undefined; }
    const t0 = Date.now();
    const iv = setInterval(() => setLoadingElapsed(Math.floor((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [loading]);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      // Se torno in foreground e il loading è ancora attivo da >5s → riprovo
      if (loading && loadingElapsed >= 5) {
        load();
        return;
      }
      // iter v33 r98 · User: «ho messo in background, riaperto, non aveva
      // fatto nulla. Poi sono uscito e rientrato ed erano fatte 4/8».
      // ROOT CAUSE: se l'app va in background durante Migliora tutte e
      // torna in foreground, i timer JS iOS sono in pausa → lo stato
      // foto locale resta vecchio. Fix: ricarico sempre dal server al
      // ritorno in foreground, così le foto migliorate lato server
      // appaiono immediatamente.
      if (!loading) {
        load();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [loading, loadingElapsed, load]);

  // iter v33 r4 — Quando il PropertyDetail è in modalità "ancora in
  // lavorazione" (foto incomplete + flag in-creazione attivo), ripollo
  // ogni 5s GET /photos così la progress bar avanza in tempo reale e la
  // schermata di standby sblocca da sola quando enhanced_count == total.
  // Il tick 1s aggiorna il timer mm:ss visibile.
  const [, setProcessingTick] = useState(0);
  useEffect(() => {
    let creatingFlag = null;
    try { creatingFlag = JSON.parse(localStorage.getItem("pronto_in_creazione_v1") || "null"); } catch { /* noop */ }
    if (!creatingFlag || creatingFlag.pid !== id) return undefined;
    // iter r95d · B2 · AbortController per la fetch dentro il polling.
    const controller = new AbortController();
    const tickTimer = setInterval(() => setProcessingTick((n) => n + 1), 1000);
    const pollTimer = setInterval(async () => {
      try {
        const { data: ph } = await api.get(`/properties/${id}/photos`, { signal: controller.signal });
        setPhotos(ph);
        // Quando tutte le foto reali sono enhanced → pulisco il flag.
        // Il banner globale farà il resto (mostra "Annuncio pronto · Apri").
        const real = (ph || []).filter((p) => !p.is_deleted && (p.photo_kind || "photo") === "photo");
        // iter v33 r18 · User M1504. Escludo le foto "perse" (original_lost)
        // e quelle già bocciate 2 volte (enhance_fail_count >= 2) dal
        // conteggio "tutte pronte": altrimenti il flag di creazione resta
        // bloccato per sempre e l'utente non può mai usare le altre azioni.
        const lavorabili = real.filter((p) => !p.original_lost && (p.enhance_fail_count || 0) < 2);
        if (lavorabili.length > 0 && lavorabili.every((p) => !!p.enhanced_path)) {
          try { localStorage.removeItem("pronto_in_creazione_v1"); } catch { /* noop */ }
          window.dispatchEvent(new Event("pronto:in-creazione"));
        }
      } catch { /* poll silenzioso */ }
    }, 5000);
    return () => {
      clearInterval(tickTimer);
      clearInterval(pollTimer);
      controller.abort();
    };
  }, [id]);

  // ── iter v28 r306 — Photo Review status check ───────────────────────
  // Quando il kit auto-enhance ha finito, il backend marca lo status come
  // "pending". Fetcho lo stato all'avvio (e su refresh tab=foto) per:
  //  - mostrare il banner sticky "Foto pronte da rivedere"
  //  - aprire automaticamente il modal se l'URL contiene ?review=1
  //    (link cliccato dalla push notification "Foto pronte da rivedere")
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    // iter r95d · B2 · AbortController per la fetch photo-review.
    const controller = new AbortController();
    (async () => {
      try {
        const { data } = await api.get(`/properties/${id}/photo-review`, { signal: controller.signal });
        if (cancelled) return;
        setPhotoReviewStatus(data?.status || null);
        // Apertura automatica via URL param (?review=1)
        if (searchParams.get("review") === "1" && data?.status !== "approved") {
          setPhotoReviewOpen(true);
          // pulisci il param dall'URL per evitare riaperture
          setSearchParams((prev) => {
            const np = new URLSearchParams(prev);
            np.delete("review");
            return np;
          }, { replace: true });
        }
      } catch (e) {
        // 404 / no data → nessuna review da fare
        if (!cancelled) setPhotoReviewStatus(null);
      }
    })();
    return () => { cancelled = true; controller.abort(); };
  // Solo all'avvio o al cambio id; il refresh post-approvazione è gestito da onApproved
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ── Auto-Autopilot when arriving from Taccuino ───────────────────────
  // The user just finished a sopralluogo and clicked "Crea annuncio".
  // Property has voice_transcript but no autopilot yet — kick off the
  // generation automatically so the agent finds title/description/services
  // already done. Fires once per property load.
  const autopilotKickedRef = useRef(false);
  useEffect(() => {
    if (!fromTaccuino) return;
    if (autopilotKickedRef.current) return;
    if (!property) return;
    if (property.autopilot) return; // already done
    if (!property.voice_transcript) return; // nothing to work from
    autopilotKickedRef.current = true;
    (async () => {
      try {
        toast.message("Genero l'annuncio dal taccuino…", {
          description: "Titolo, descrizione e servizi vicini · pochi secondi",
          duration: 4000,
        });
        const { data } = await api.post(`/properties/${id}/autopilot`, { tone: "professionale" });
        setAutopilot(data.autopilot);
        setProperty((p) => ({ ...p, autopilot: data.autopilot }));
        toast.success("Annuncio pronto! Controlla l'analisi.");
      } catch (e) {
        // Best-effort: never break the user flow.
        console.warn("auto-autopilot failed:", e?.response?.data || e);
      }
    })();
  }, [fromTaccuino, property, id]);

  // Load listing once on mount for the workflow-hint banner (isPublic state)
  // r95j (feb 2026) · D.8 · Rimosso prefetch aggressivo (portal-pack + social-
  // assets + social/manifest) al mount ad ogni apertura annuncio. Costava ~500KB
  // + 3 fetch parallele in competition col first paint, per endpoint spesso non
  // usati (agente entra e resta su Foto/Descrizione). Il tab Social/Vetrina
  // fa il fetch alla propria mount: primo click ~200ms piu' lento, ma il resto
  // del PropertyDetail apre visibilmente piu' veloce.
  useEffect(() => {
    api.get(`/properties/${id}/listing`).then(({ data }) => setListing(data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (tab === "autopilot") {
      if (property?.autopilot && !autopilot) setAutopilot(property.autopilot);
      if (property?.voice_transcript && !voiceTranscript) setVoiceTranscript(property.voice_transcript);
    }
    if (tab === "portali" && !portalLinks) {
      api.get(`/properties/${id}/portals`).then(({ data }) => setPortalLinks(data)).catch(() => {});
    }
    if (tab === "portali" && property?.portal_pack && !pack) {
      setPack(property.portal_pack);
    }
    if (tab === "lead") {
      api.get(`/properties/${id}/viewings`).then(({ data }) => setViewings(data.viewings || [])).catch(() => {});
      // iter 275 round 52: anche i lead "solo email" (senza prenotazione visita)
      // devono comparire nel tab Contatti ricevuti.
      api.get(`/properties/${id}/leads`).then(({ data }) => setLeads(Array.isArray(data) ? data : (data?.leads || []))).catch(() => {});
    }
    if (tab === "condivisione") {
      api.get(`/properties/${id}/listing`).then(({ data }) => setListing(data)).catch(() => {});
      api.get(`/properties/${id}/stats`).then(({ data }) => setStats(data)).catch(() => {});
    }
  }, [tab, id, portalLinks, property?.portal_pack, pack]);

  const onGeneratePortalPack = async () => {
    setPackLoading(true);
    try {
      const { data } = await api.post(`/properties/${id}/portal-pack`, { tone });
      setPack(data.pack);
      setProperty((p) => ({ ...p, portal_pack: data.pack }));
      toast.success("Pacchetto annunci generato!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore generazione pacchetto");
    } finally { setPackLoading(false); }
  };

  const onUpdateViewing = async (vid, status) => {
    try {
      await api.patch(`/viewings/${vid}`, { status });
      setViewings((arr) => arr.map((v) => (v.id === vid ? { ...v, status } : v)));
    } catch { toast.error("Errore aggiornamento"); }
  };

  const [editOpen, setEditOpen] = useState(false);

  // ── Share with client ──────────────────────────────────────────────────
  // Two URLs:
  //  - shareLink: /api/share/{id} → smart redirect (bot → OG HTML, human → SPA)
  //               this is what we paste into WhatsApp/email/native share so previews render
  //  - shareLinkHuman: /listing/{id} → direct SPA URL, used for "Apri come cliente"
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [shareLinkHuman, setShareLinkHuman] = useState("");
  const onOpenShare = async () => {
    try {
      let listing = await api.get(`/properties/${id}/listing`).then(r => r.data);
      if (!listing?.exists) {
        listing = await api.post(`/properties/${id}/listing`).then(r => r.data);
      }
      const origin = window.location.origin;
      setShareLink(`${origin}/api/share/${listing.public_id}`);
      setShareLinkHuman(`${origin}/listing/${listing.public_id}`);
      setShareOpen(true);
      // If we just created the listing now, open the proactive match modal.
      if (listing.just_created) {
        setMatchModalShareLink(`${origin}/listing/${listing.public_id}`);
        setMatchModalOpen(true);
      }
    } catch {
      toast.error("Impossibile creare il link di condivisione");
    }
  };
  const copyShareLink = async () => {
    // iter 313 r17 — robust clipboard (iOS fallback)
    const r = await copyToClipboard(shareLink, { shareTitle: "Link condivisione" });
    if (r === "clipboard") toast.success("Link copiato!");
    else if (r === "manual") toast.error("Copia non riuscita");
  };
  const shareWhatsapp = () => {
    const txt = `${property?.title || "Immobile"} — guarda foto, pianta e dettagli:\n${shareLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank");
  };
  const shareEmail = () => {
    const subject = property?.title || "Immobile";
    const body = `Ciao,\n\nti mando le informazioni complete sull'immobile (foto migliorate, pianta arredata, brochure):\n${shareLink}\n\nA presto`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, "_blank");
  };
  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: property?.title || "Immobile",
          text: `${property?.title || "Immobile"} — foto, pianta e dettagli`,
          url: shareLink,
        });
      } catch {/* user cancelled */}
    } else {
      copyShareLink();
    }
  };

  // ── Autopilot ───────────────────────────────────────────────────────────
  const onRunAutopilot = async () => {
    setAutopilotLoading(true);
    try {
      const { data } = await api.post(`/properties/${id}/autopilot`, { tone });
      setAutopilot(data.autopilot);
      setProperty((p) => ({ ...p, autopilot: data.autopilot }));
      toast.success("Analisi completata · scorri sotto per vedere annuncio, voto e cosa migliorare");
      // iter 310 r18 — auto-scroll all'annuncio pronto così l'utente vede
      // immediatamente cosa è cambiato (era facile "perdersi" dopo il click).
      setTimeout(() => {
        const el = document.querySelector('[data-testid="autopilot-listing"]')
          || document.querySelector('[data-testid="autopilot-hero"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore analisi");
    } finally { setAutopilotLoading(false); }
  };

  const onStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      recChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) recChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(recChunksRef.current, { type: "audio/webm" });
        if (blob.size === 0) return;
        setVoiceUploading(true);
        const fd = new FormData();
        fd.append("file", blob, "voice.webm");
        try {
          const { data } = await api.post(`/properties/${id}/transcribe-voice`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setVoiceTranscript(data.transcript);
          setProperty((p) => ({ ...p, voice_transcript: data.transcript }));
          toast.success("Nota trascritta · rianalizzo l'annuncio…");
          // iter v28 r20 — Auto-rianalisi: l'utente non deve premere "Rianalizza"
          // separatamente. Appena la trascrizione torna, parte subito l'analisi
          // qualità tenendo conto della nuova nota vocale.
          setTimeout(() => {
            // setTimeout 0 per uscire dallo stack della response e lasciare
            // che setVoiceTranscript renderizzi prima
            onRunAutopilot();
          }, 0);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Errore trascrizione");
        } finally { setVoiceUploading(false); }
      };
      mr.start();
      mediaRecRef.current = mr;
      setVoiceRecording(true);
    } catch (err) {
      toast.error("Microfono non accessibile");
    }
  };

  const onStopRecording = () => {
    if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") {
      mediaRecRef.current.stop();
    }
    setVoiceRecording(false);
  };

  const onActivateListing = async () => {
    try {
      const { data } = await api.post(`/properties/${id}/listing`);
      setListing({ ...data, exists: true, views: 0, leads: 0 });
      // iter 259: toast esplicito per nuovo utente — chiarisce che ora
      // l'annuncio è LIVE + dove trovare brochure/social.
      toast.success("Annuncio online! Puoi già condividerlo. Foto e post social li trovi in Scarica file.", {
        duration: 5500,
        action: {
          label: "Scarica file",
          onClick: () => setTab("file"),
        },
      });
      if (data.just_created) {
        const origin = window.location.origin;
        setMatchModalShareLink(`${origin}/listing/${data.public_id}`);
        setMatchModalOpen(true);
      }
    } catch (err) {
      toast.error("Errore attivazione");
    }
  };

  const onToggleListing = async () => {
    try {
      const { data } = await api.put(`/properties/${id}/listing/toggle`);
      setListing((l) => ({ ...(l || {}), ...data, exists: true }));
      toast.success(data.is_public ? "Pagina riattivata" : "Pagina nascosta");
    } catch (err) { toast.error("Errore"); }
  };

  // iter 261: elimina definitivamente la pagina pubblica (link non funziona
  // più, lead e visite cancellati). L'immobile rimane intatto — può essere
  // ripubblicato in qualsiasi momento.
  //
  // iter v28 r19 — Bug mobile risolto: `window.confirm()` su iOS Safari/PWA
  // veniva bloccato silenziosamente. Sostituito con AlertDialog shadcn che
  // funziona su ogni browser e dispositivo (modal in-app, sempre).
  const [deleteListingOpen, setDeleteListingOpen] = useState(false);
  const [deleteListingBusy, setDeleteListingBusy] = useState(false);
  const onDeleteListing = () => setDeleteListingOpen(true);
  const confirmDeleteListing = async () => {
    setDeleteListingBusy(true);
    try {
      await api.delete(`/properties/${id}/listing`);
      setListing({ exists: false });
      toast.success("Pagina pubblica eliminata");
      setDeleteListingOpen(false);
    } catch (err) {
      toast.error("Errore eliminazione");
    } finally {
      setDeleteListingBusy(false);
    }
  };

  // iter 313 r9 — publicListingUrl resta `/listing/X` (display all'agente).
  // Per CONDIVISIONE (clipboard, WhatsApp, navigator.share) il PropertyDetail
  // usa `publicShareUrl` (`/api/share/X`) così i crawler social ricevono i
  // meta OG e WhatsApp mostra il preview ricco con foto+titolo+descrizione.
  const publicListingUrl = listing?.public_id ? `${window.location.origin}/listing/${listing.public_id}` : "";
  const publicShareUrl = listing?.public_id ? `${window.location.origin}/api/share/${listing.public_id}` : "";

  const copyPublicLink = async () => {
    if (!publicShareUrl) return;
    // iter 313 r17 — robust clipboard (iOS fallback)
    const r = await copyToClipboard(publicShareUrl, { shareTitle: "Link annuncio" });
    if (r === "clipboard") {
      setLinkCopied(true);
      toast.success("Link copiato · pronto per WhatsApp");
      setTimeout(() => setLinkCopied(false), 1800);
    } else if (r === "manual") {
      toast.error("Impossibile copiare. Seleziona e copia manualmente.");
    }
    // "share" → l'OS ha gestito tutto
  };

  const downloadQr = async (kind = "listing") => {
    const token = Cookies.get("vista_jwt");
    const qs = new URLSearchParams();
    if (token) qs.append("auth", token);
    if (kind === "whatsapp") qs.append("kind", "whatsapp");
    const url = `${API}/properties/${id}/qr?${qs.toString()}`;
    try {
      const r = await fetch(url, {
        headers: { "x-vista-origin": window.location.origin, Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const txt = await r.text();
        let detail = "Errore download QR";
        try { detail = JSON.parse(txt)?.detail || detail; } catch { /* not json */ }
        throw new Error(detail);
      }
      const blob = await r.blob();
      const filename = kind === "whatsapp" ? `pronto-qr-whatsapp-${id}.png` : `pronto-qr-${id}.png`;
      // iter 313 r18 — `a.click()` con download non funziona su iOS Safari.
      // smartDownloadBlob apre share-sheet su iOS (Allega in File/WhatsApp),
      // download classico su desktop/Android.
      await smartDownloadBlob(blob, filename, "image/png");
      if (isIOSDevice()) {
        toast.success("QR pronto. Tocca 'Condividi' per salvarlo o inviarlo.");
      } else {
        toast.success("QR scaricato.");
      }
    } catch (err) {
      toast.error(err?.message || "Errore download QR");
    }
  };
  const downloadQrWa = () => downloadQr("whatsapp");

  const onEstimate = async () => {
    setEstimating(true);
    try {
      const { data } = await api.post(`/properties/${id}/estimate`);
      setEstimate(data);
      toast.success("Stima pronta");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore stima");
    } finally { setEstimating(false); }
  };

  const onFilePick = () => fileInputRef.current?.click();
  const onPlanFilePick = () => planInputRef.current?.click();

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // iter 259: pre-check euristico — se almeno una sembra una pianta,
    // chiediamo conferma all'agente prima di caricarla come foto.
    // Backstop: il check costa < 30ms per foto e zero soldi (no AI).
    // iter v25 — sostituito window.confirm (bloccato su iOS PWA) con toast
    // action button. Se l'agente non agisce in 8s la foto viene caricata come photo.
    const flagged = [];
    for (let i = 0; i < files.length; i++) {
      if (await isLikelyFloorPlan(files[i])) flagged.push({ index: i, name: files[i].name });
    }
    if (flagged.length > 0) {
      const askPlan = flagged.length === files.length
        ? "Sembra una planimetria. Vuoi caricarla nel tab Pianta?"
        : `${flagged.length} ${flagged.length === 1 ? "foto sembra una planimetria" : "foto sembrano planimetrie"}. Spostarl${flagged.length === 1 ? "a" : "e"} nel tab Pianta?`;
      const goPlan = await new Promise((resolve) => {
        let decided = false;
        const decide = (val) => { if (!decided) { decided = true; resolve(val); } };
        toast.warning(askPlan, {
          duration: 12000,
          action: {
            label: "Vai a Pianta",
            onClick: () => decide(true),
          },
          cancel: {
            label: "Carica come foto",
            onClick: () => decide(false),
          },
          onDismiss: () => decide(false),
          onAutoClose: () => decide(false),
        });
      });
      if (goPlan) {
        setTab("pianta");
        toast.info("Tocca \"Carica planimetria\" per importarla nel tab giusto", { duration: 4500 });
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: files.length, phase: "compressing" });
    try {
      for (let i = 0; i < files.length; i++) {
        const original = files[i];
        // Iter 249: compressione client-side prima dell'upload (riduce
        // foto iPhone 8-15 MB → 1-2 MB, upload 3-4x più veloce su 4G).
        setUploadProgress({ current: i, total: files.length, phase: "compressing" });
        const f = await compressPhoto(original);
        const fd = new FormData();
        fd.append("file", f);
        setUploadProgress({ current: i, total: files.length, phase: "uploading" });
        const { data } = await api.post(`/properties/${id}/photos?kind=photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (evt.total) {
              setUploadProgress((p) => ({ ...p, pct: Math.round((evt.loaded / evt.total) * 100) }));
            }
          },
        });
        setPhotos((ps) => [data, ...ps]);
        if (!activePhotoId) setActivePhotoId(data.id);
      }
      toast.success(`${files.length} foto caricate`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fallito");
    } finally {
      setUploading(false);
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onUploadPlan = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setUploadProgress({ current: 0, total: files.length, phase: "compressing" });
    try {
      for (let i = 0; i < files.length; i++) {
        const original = files[i];
        setUploadProgress({ current: i, total: files.length, phase: "compressing" });
        const f = await compressPhoto(original);
        const fd = new FormData();
        fd.append("file", f);
        setUploadProgress({ current: i, total: files.length, phase: "uploading" });
        const { data } = await api.post(`/properties/${id}/photos?kind=plan`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (evt.total) {
              setUploadProgress((p) => ({ ...p, pct: Math.round((evt.loaded / evt.total) * 100) }));
            }
          },
        });
        setPhotos((ps) => [data, ...ps]);
        setActivePlanId(data.id);
      }
      toast.success(files.length === 1 ? "Planimetria caricata" : `${files.length} planimetrie caricate`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fallito");
    } finally {
      setUploading(false);
      if (planInputRef.current) planInputRef.current.value = "";
    }
  };

  // Pre-flight check: warn the agent on low-quality input or repeated retries
  // BEFORE we burn another AI credit. Returns false → user cancelled.
  const _runPrecheckOrConfirm = async (photo) => {
    let pre = null;
    try {
      const { data } = await api.post(`/photos/${photo.id}/precheck`);
      pre = data;
    } catch { /* precheck is advisory — don't block on its own failure */ }

    const enhanceCount = pre?.enhance_count ?? (photo.enhance_count || 0);
    const warnings = (pre?.warnings || []).filter((w) => w.level === "warning");

    // iter v25 — window.confirm() bloccato silenziosamente su iOS Safari PWA
    // → ritornava undefined (falsy) → blocco totale del flow Arreda/Migliora.
    // Sostituito con toast.warning con action button (sonner). Default = procedi
    // (ottimismo: se l'utente non interagisce in 12s vuol dire "vai avanti").
    const _askToast = (msg) => new Promise((resolve) => {
      let decided = false;
      const decide = (v) => { if (!decided) { decided = true; resolve(v); } };
      toast.warning(msg, {
        duration: 12000,
        action: { label: "Continua", onClick: () => decide(true) },
        cancel: { label: "Annulla", onClick: () => decide(false) },
        onDismiss: () => decide(true),    // dismiss = procedi (ottimismo)
        onAutoClose: () => decide(true),  // timeout = procedi
      });
    });

    if (warnings.length) {
      const lines = warnings.map((w) => `• ${w.msg}`).join(" ");
      const isPlan = !!pre?.is_floor_plan;
      const message = isPlan
        ? `Pianta con qualche limite: ${lines} · Procedi comunque?`
        : `Foto con qualche limite: ${lines} · Procedi comunque?`;
      const ok = await _askToast(message);
      if (!ok) return false;
    }
    if (enhanceCount >= 2) {
      const ok = await _askToast(
        `Hai già migliorato questa foto ${enhanceCount} volte. Continuare costa un credito. Procedere?`
      );
      if (!ok) return false;
    }
    return true;
  };

  // iter 256 helper: traduce la modalità UI in `focus` da inviare al backend.
  // Backend `_parse_focus` accetta string CSV ("luce,colori") o single
  // ("completo"|"fedele"). Vedi /app/backend/ai_prompts.py:_parse_focus.
  const currentFocusPayload = () => {
    if (enhanceMode === "fedele") return "fedele";
    if (enhanceMode === "custom" && enhanceFoci.length > 0) return enhanceFoci.join(",");
    // iter v33 r107 · Se l'utente ha selezionato "Solo luce" nel segmented,
    // manda focus=keep_objects (preserva scena, stessa qualità fotografica).
    if (enhanceFocus === "solo_luce") return "keep_objects";
    return "completo"; // auto = completo (aggressivo, applica tutto)
  };

  const onEnhance = async (photo) => {
    if (!(await _runPrecheckOrConfirm(photo))) return;
    setEnhancingId(photo.id);
    // iter v32 r26 — User: «se metto l'app in background dà errore».
    // Su Capacitor iOS la webview viene sospesa dopo ~30s in background:
    // la fetch promise non risolve mai e l'utente vede "Errore generazione".
    // Strategia: ascolto `visibilitychange` durante la chiamata; se l'app
    // torna in foreground e nel frattempo era stata sospesa, rifetcho lo
    // stato della foto (il backend ha già completato in background).
    const inflightKey = `enhance:inflight:${photo.id}`;
    try { localStorage.setItem(inflightKey, String(Date.now())); } catch { /* */ }
    const onVisChange = async () => {
      if (document.visibilityState !== "visible") return;
      // App tornata in primo piano: ricarica le foto dal server, magari
      // l'enhance è già finito anche se la fetch originale è morta.
      try {
        const { data: fresh } = await api.get(`/properties/${id}/photos`);
        const updated = (fresh || []).find((p) => p.id === photo.id);
        if (updated?.enhanced_path) {
          setPhotos((ps) => ps.map((p) => (p.id === photo.id ? updated : p)));
        }
      } catch { /* ignore */ }
    };
    document.addEventListener("visibilitychange", onVisChange);
    try {
      const focus = currentFocusPayload();
      const body = { focus };
      // iter v33 r46 · Gestione risposta 202 (photo ancora in lavorazione,
      // Nano Banana lento). Il task continua in background lato server e
      // salvera' la foto quando finisce. Qui pollo /properties/{id}/photos
      // ogni 3s finche' `enhanced_path` cambia (o timeout 3 min).
      // r95j-hotfix2 (feb 2026) · Timeout axios override: 95s > backend
      // 80s wait_for + margine. Con validator Pro (Fix 2) + eventuale
      // retry, il flusso sync arriva vicino ai 60s del timeout globale
      // e l'utente vedeva "timeout of 60000ms exceeded" anche se il
      // backend stava per rispondere 202 e continuare in background.
      const resp = await api.post(`/photos/${photo.id}/enhance`, body, {
        timeout: 95000,
        // Non trattiamo 202 come errore.
        validateStatus: (s) => (s >= 200 && s < 300),
      });
      let data = resp?.data;
      if (resp?.status === 202) {
        toast.info("Foto in lavorazione…", {
          description: "Il ritocco richiede più tempo del solito, aspetta qualche secondo.",
          duration: 4000,
        });
        // Polling: massimo 3 minuti (60 tentativi ogni 3s).
        const prevEnhancedPath = photo.enhanced_path || null;
        let found = null;
        for (let attempt = 0; attempt < 60; attempt++) {
          await new Promise((r) => setTimeout(r, 3000));
          try {
            const { data: fresh } = await api.get(`/properties/${id}/photos`);
            const updated = (fresh || []).find((p) => p.id === photo.id);
            if (updated?.enhanced_path && updated.enhanced_path !== prevEnhancedPath) {
              found = updated;
              break;
            }
          } catch { /* keep polling */ }
        }
        if (!found) {
          toast.error("Foto ancora in lavorazione · ricarica tra 1 minuto per vedere il risultato");
          return;
        }
        data = found;
      }
      // iter 298: prefetch dell'enhanced_path PRIMA di re-renderizzare la
      // galleria. L'utente segnalava "le thumb restano bianche un bel po'":
      // succedeva perché setPhotos forzava un new <img src=...> che doveva
      // ancora scaricare. Pre-caricando in cache il browser ce l'ha già pronto.
      if (data?.enhanced_path) {
        try { new Image().src = authedFileUrl(data.enhanced_path); } catch { /* noop */ }
      }
      setPhotos((ps) => ps.map((p) => (p.id === photo.id ? data : p)));
      const fid = data.fidelity;
      if (fid?.verdict === "warn") {
        toast.warning("Foto migliorata · controlla l'anteprima", {
          description: fid.reasons?.[0] || "Lievi differenze rilevate",
        });
      } else {
        toast.success("Foto migliorata · fedele all'originale", data.free_retry ? { description: "Rigenerazione gratuita · non scalata dal piano" } : undefined);
      }
    } catch (err) {
      // iter v32 r26 — Recovery se l'errore è dovuto a webview sospesa.
      // Quando l'app è stata in background durante la chiamata, la fetch
      // muore con AbortError o NetworkError. Prima di mostrare errore,
      // chiediamo al server se la foto è stata comunque migliorata.
      try {
        const { data: fresh } = await api.get(`/properties/${id}/photos`);
        const updated = (fresh || []).find((p) => p.id === photo.id);
        if (updated?.enhanced_path && updated.enhanced_path !== photo.enhanced_path) {
          setPhotos((ps) => ps.map((p) => (p.id === photo.id ? updated : p)));
          toast.success("Foto migliorata", { description: "Pronto ha completato il lavoro mentre eri in altre app." });
          return;
        }
      } catch { /* fall through to normal error */ }
      const detail = err?.response?.data?.detail;
      if (typeof detail === "object" && detail?.message) {
        toast.error(detail.message, { description: "Prova la modalità Ultra Fedele dalle Opzioni." });
      } else {
        toast.error(detail || "Errore generazione");
      }
    } finally {
      document.removeEventListener("visibilitychange", onVisChange);
      try { localStorage.removeItem(inflightKey); } catch { /* */ }
      setEnhancingId(null);
    }
  };

  const onStage = async (
    photo,
    style = stageStyle,
    roomHint = stageRoom,
    changeFloor = false,
    floorStyle = null,
    opts = {},
  ) => {
    if (!(await _runPrecheckOrConfirm(photo))) return;
    setStagingId(photo.id);
    try {
      const payload = { style, change_floor: !!changeFloor };
      if (roomHint && roomHint.trim()) payload.room_hint = roomHint.trim();
      if (changeFloor && floorStyle) payload.floor_style = floorStyle;
      if (opts.confirmNotEmpty) payload.confirm_not_empty = true;
      const { data } = await api.post(`/photos/${photo.id}/stage`, payload);
      setPhotos((ps) => ps.map((p) => (p.id === photo.id ? data : p)));
      setStyleConsistency((c) => ({ ...(c || {}), stage: { style, change_floor: changeFloor, floor_style: floorStyle, updated_at: new Date().toISOString() } }));
      const floorMsg = changeFloor ? " · pavimento nuovo" : "";
      const roomMsg = roomHint ? ` · ${roomHint}` : "";
      toast.success(`Stanza arredata · ${style}${roomMsg}${floorMsg}`, data.free_retry ? { description: "Rigenerazione gratuita · non scalata dal piano" } : undefined);
    } catch (err) {
      // iter v28 r93 — Se il backend rifiuta perché la stanza è già arredata
      // (status 422), proponiamo all'utente di passare a "Cambia arredo"
      // oppure di procedere comunque.
      if (err?.response?.status === 422 && !opts.confirmNotEmpty) {
        toast.warning("Questa stanza sembra già arredata", {
          description: "Usa 'Cambia arredo' per cambiare l'arredo esistente, oppure procedi comunque.",
          duration: 9000,
          action: {
            label: "Procedi comunque",
            onClick: () => onStage(photo, style, roomHint, changeFloor, floorStyle, { confirmNotEmpty: true }),
          },
        });
      } else {
        toast.error(err?.response?.data?.detail || "Errore arredo");
      }
    } finally { setStagingId(null); }
  };

  const onRestyle = async (
    photo,
    style = restyleStyle,
    roomHint = restyleRoom,
    changeFloor = restyleChangeFloor,
    floorStyle = restyleFloorStyle,
  ) => {
    if (!(await _runPrecheckOrConfirm(photo))) return;
    setRestyleId(photo.id);
    try {
      const payload = {
        style,
        change_floor: !!changeFloor,
      };
      if (roomHint && roomHint.trim()) payload.room_hint = roomHint.trim();
      if (changeFloor && floorStyle) payload.floor_style = floorStyle;
      const { data } = await api.post(`/photos/${photo.id}/restyle`, payload);
      setPhotos((ps) => ps.map((p) => (p.id === photo.id ? data : p)));
      setStyleConsistency((c) => ({ ...(c || {}), restyle: { style, change_floor: changeFloor, floor_style: floorStyle, updated_at: new Date().toISOString() } }));
      const floorMsg = changeFloor ? " · pavimento nuovo" : "";
      const roomMsg = roomHint ? ` · ${roomHint}` : "";
      toast.success(`Arredo rinnovato · ${style}${roomMsg}${floorMsg}`, data.free_retry ? { description: "Rigenerazione gratuita · non scalata dal piano" } : undefined);
      setShowRestyle(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore restyling");
    } finally { setRestyleId(null); }
  };

  // iter v28 r390 — Svuota stanza (stanza arredata → vuota, mantenendo
  // architettura). Endpoint backend: POST /api/photos/{id}/empty-room.
  const onEmptyRoom = async (photo, roomHint = emptyRoomHint) => {
    if (!(await _runPrecheckOrConfirm(photo))) return;
    setEmptyRoomId(photo.id);
    try {
      const payload = {};
      if (roomHint && roomHint.trim()) payload.room_hint = roomHint.trim();
      const { data } = await api.post(`/photos/${photo.id}/empty-room`, payload);
      setPhotos((ps) => ps.map((p) => (p.id === photo.id ? data : p)));
      const roomMsg = roomHint ? ` · ${roomHint}` : "";
      toast.success(`Stanza svuotata${roomMsg}`, data.free_retry ? { description: "Rigenerazione gratuita · non scalata dal piano" } : undefined);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore svuota stanza");
    } finally { setEmptyRoomId(null); }
  };

  // iter v33 r30 · State per il gate crediti + resume async batch job.
  // iter v33 r32 · `activeBatchJobId` traccia il job attivo cosi' il
  // bottone "Annulla" durante Migliora tutte sa quale job fermare.
  const [lowCreditsInfo, setLowCreditsInfo] = useState(null); // {balance, required, photoCount} | null
  const [pendingBatchLaunch, setPendingBatchLaunch] = useState(null); // { pending, opts } salvato per riprendere dopo scelta
  const [activeBatchJobId, setActiveBatchJobId] = useState(null);
  const [cancellingBatch, setCancellingBatch] = useState(false);
  const { cost: costPerPhotoEnhance, isUnlimited: creditsUnlimited } = useEnhanceCredits("photo_enhance");

  // Poll ricorrente per un batch job attivo (funziona anche se l'agente
  // aveva chiuso l'app: alla riapertura questa funzione trova il job
  // ancora `running` e riprende la barra progresso da dove era rimasta).
  const pollBatchJob = useCallback(async (jobId) => {
    let last = null;
    // r95j (feb 2026) · D.6 · retry counter: 3 errori consecutivi prima
    // di rompere il loop, altrimenti un singolo blip di rete fermava il
    // poll di recovery.
    let consecutiveErrs = 0;
    for (let attempt = 0; attempt < 600; attempt++) {  // max ~30 min
      try {
        const { data } = await api.get(`/photos/enhance-batch-status/${jobId}`);
        last = data;
        consecutiveErrs = 0;
        setBatchProgress({ done: data.done || 0, total: data.total || 0, errors: data.errors || 0 });
        if (data.status !== "running") break;
      } catch {
        consecutiveErrs += 1;
        if (consecutiveErrs >= 3) break;
      }
      await new Promise((res) => setTimeout(res, 3000));
    }
    return last;
  }, []);

  const _runBatchAsync = useCallback(async (pending, opts, modeLabel) => {
    const tid = "batch-enhance";
    setBatchLoading(true);
    setBatchProgress({ done: 0, total: pending.length, errors: 0, in_progress: 0 });
    // iter v33 r36 · No piu' toast.loading persistente in alto (copre
    // il menu). Il progresso si vede su:
    //  - BatchProgressOverlay dentro l'annuncio (schermo intero)
    //  - BatchTrayPill globale in basso quando si esce dall'annuncio
    //  - Toast singolo di completamento generato da BatchCompletionToaster
    let jobId = null;
    try {
      const { data } = await api.post(
        `/properties/${id}/photos/enhance-batch-start`,
        { photo_ids: pending.map((p) => p.id), ...opts },
      );
      jobId = data?.job_id;
      if (!jobId) throw new Error("no_job_id");
      setActiveBatchJobId(jobId);
      // Poll loop — la UI si aggiorna live
      let ticker = null;
      // iter v33 r35 · Polling PIU' RAPIDO all'inizio (1.5s) cosi' vedi
      // subito il "in_progress" salire non appena il worker prende in
      // carico le prime foto; poi torna a 3s per non stressare il DB.
      const _POLL_MS = 1500;
      // r95j (feb 2026) · D.6 · retry counter: prima un errore transitorio
      // (proxy 502, connessione mobile in switch) fermava il ticker e
      // congelava la progress bar. Ora tolleriamo 3 errori consecutivi
      // prima di staccare.
      let consecutiveErrs = 0;
      ticker = setInterval(async () => {
        try {
          const { data: s } = await api.get(`/photos/enhance-batch-status/${jobId}`);
          consecutiveErrs = 0;
          setBatchProgress({
            done: s.done || 0,
            total: s.total || 0,
            errors: s.errors || 0,
            in_progress: s.in_progress || 0,
          });
          if (s.status !== "running") {
            clearInterval(ticker);
            ticker = null;
            invalidateEnhanceCreditsCache();
            // iter v33 r36 · La notifica finale la fa BatchCompletionToaster
            // (globale) via tracker: qui basta chiudere l'overlay + reload.
            await load();
            setBatchLoading(false);
            setBatchProgress(null);
            setActiveBatchJobId(null);
            setCancellingBatch(false);
          } else {
            // Aggiorna la galleria ogni 6s cosi' vedi le foto nuove uscire
            if ((s.done || 0) > 0 && (s.done || 0) % 2 === 0) {
              load();
            }
          }
        } catch (pollErr) {
          consecutiveErrs += 1;
          if (consecutiveErrs >= 3) {
            clearInterval(ticker);
            ticker = null;
            setBatchLoading(false);
            setBatchProgress(null);
            setActiveBatchJobId(null);
            setCancellingBatch(false);
          }
        }
      }, _POLL_MS);
    } catch (err) {
      // iter v33 r98 · User: «prima è uscito Network Error però il server
      // aveva preso in carico il job e ha migliorato 4/8 foto». ROOT CAUSE:
      // la POST /enhance-batch-start ha risposto ma la connessione mobile
      // ha perso la risposta HTTP → axios lancia network error → mostriamo
      // "Errore avvio batch" all'utente MA il server sta già lavorando.
      // Fix: se l'errore è puro network (no response body), aspetto 2s e
      // controllo se c'è un batch attivo per questa property. Se sì, il
      // job è partito → NON mostro errore, aggancio il polling e continuo.
      const isNetworkErr = !err?.response;
      if (isNetworkErr) {
        try {
          await new Promise((r) => setTimeout(r, 2000));
          const { data } = await api.get("/photos/enhance-batch-active-mine");
          const active = (data?.jobs || []).find((j) => j.property_id === id);
          if (active?.job_id) {
            // Job effettivamente partito → aggancio il polling e continuo
            const recoveredJobId = active.job_id;
            setActiveBatchJobId(recoveredJobId);
            let ticker = null;
            const _POLL_MS = 1500;
            ticker = setInterval(async () => {
              try {
                const { data: s } = await api.get(`/photos/enhance-batch-status/${recoveredJobId}`);
                setBatchProgress({
                  done: s.done || 0,
                  total: s.total || 0,
                  errors: s.errors || 0,
                  in_progress: s.in_progress || 0,
                });
                if (s.status !== "running") {
                  clearInterval(ticker);
                  ticker = null;
                  invalidateEnhanceCreditsCache();
                  await load();
                  setBatchLoading(false);
                  setBatchProgress(null);
                  setActiveBatchJobId(null);
                  setCancellingBatch(false);
                } else if ((s.done || 0) > 0 && (s.done || 0) % 2 === 0) {
                  load();
                }
              } catch {
                clearInterval(ticker);
                ticker = null;
                setBatchLoading(false);
                setBatchProgress(null);
                setActiveBatchJobId(null);
                setCancellingBatch(false);
              }
            }, _POLL_MS);
            return;  // NON mostro errore
          }
        } catch { /* fallback al toast di errore */ }
      }
      setBatchLoading(false);
      setBatchProgress(null);
      setActiveBatchJobId(null);
      const msg = err?.response?.data?.detail || err?.message || "Errore avvio batch";
      toast.error(msg);
    }
  }, [id, load]);

  // iter v33 r32 · Cancel handler — l'agente ha premuto "Annulla" durante
  // Migliora tutte. Chiedo al backend di marcare il job come cancelled;
  // il worker vede lo status e si ferma tra 3-5 secondi (le foto in
  // corso finiscono, le successive non partono). Il polling normale
  // gestira' la transizione UI.
  const onCancelBatch = useCallback(async () => {
    if (!activeBatchJobId || cancellingBatch) return;
    setCancellingBatch(true);
    try {
      await api.post(`/photos/enhance-batch-cancel/${activeBatchJobId}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Impossibile annullare";
      toast.error(msg);
      setCancellingBatch(false);
    }
  }, [activeBatchJobId, cancellingBatch]);

  const onBatchEnhance = async () => {
    const pending = photos.filter((p) => (p.photo_kind || "photo") === "photo" && !p.enhanced_path && !p.is_deleted && !p.original_lost);
    if (pending.length === 0) {
      toast.info("Tutte le foto sono già state migliorate");
      return;
    }
    const focus = currentFocusPayload();
    const e = styleConsistency?.enhance;
    const opts = e ? { intensity: e.intensity, style: e.style, focus } : { focus };
    const modeLabel = enhanceMode === "fedele" ? "Ultra fedele"
      : enhanceMode === "custom" && enhanceFoci.length > 0 ? `Personalizzato (${enhanceFoci.length} aspetti)`
      : "Automatico";

    // iter v33 r30 · Gate crediti — se non bastano, apri modal.
    if (!creditsUnlimited) {
      try {
        const { data: me } = await api.get("/credits/me");
        const balance = Number(me?.balance_total ?? 0);
        const required = pending.length * (costPerPhotoEnhance || 1);
        if (balance < required) {
          setLowCreditsInfo({
            balance,
            required,
            photoCount: pending.length,
            costPerPhoto: costPerPhotoEnhance || 1,
          });
          setPendingBatchLaunch({ pending, opts, modeLabel });
          return;
        }
      } catch { /* se fallisce lettura crediti, procedi comunque */ }
    }

    await _runBatchAsync(pending, opts, modeLabel);
  };

  const onFurnishPlan = async (photo) => {
    if (!(await _runPrecheckOrConfirm(photo))) return;
    setFurnishingId(photo.id);
    // iter v25 — `planStyle` può essere "casuale" → risolto in uno stile
    // concreto random prima di chiamare il backend (Gemini non riconosce "casuale").
    const styleToSend = resolveFurnishStyle(planStyle);
    try {
      const { data } = await api.post(`/photos/${photo.id}/furnish-plan`, { style: styleToSend });
      setPhotos((ps) => ps.map((p) => (p.id === photo.id ? data : p)));
      toast.success(`Pianta arredata (stile ${styleToSend})`, data.free_retry ? { description: "Rigenerazione gratuita · non scalata dal piano" } : undefined);
    } catch (err) {
      // iter v26 — usa apiError centralizzato (estrae sempre stringa, anche
      // se backend ritorna detail come oggetto es. crediti insufficienti).
      console.error("[onFurnishPlan] failed", err?.response?.status, err?.response?.data, err);
      toast.error(apiError(err, "Errore arredamento pianta"));
    } finally { setFurnishingId(null); }
  };

  const onReorder = async (orderedIds) => {
    // iter v28 r90 — Drag & drop riordino: optimistic UI + PATCH server.
    // iter v33 r26 · User: «Errore salvataggio in ordine — riprova al prossimo
    // refresh» fa venire ansia. Ora il salvataggio e' completamente silenzioso:
    // se fallisce riprova in background 3 volte con backoff, poi si arrende
    // senza toast rosso (l'ordine locale resta comunque quello che l'utente
    // vede, si risincronizza al prossimo load).
    if (!Array.isArray(orderedIds) || orderedIds.length === 0) return;
    const idSet = new Set(orderedIds);
    const reordered = orderedIds.map((id) => photos.find((p) => p.id === id)).filter(Boolean);
    const rest = photos.filter((p) => !idSet.has(p.id));
    setPhotos([...reordered, ...rest]);
    const attempt = async (n) => {
      try {
        await api.post(`/properties/${id}/photos/reorder`, { photo_ids: orderedIds });
      } catch (err) {
        if (n < 3) {
          const delay = 800 * Math.pow(2, n);
          setTimeout(() => attempt(n + 1), delay);
        } else {
          console.warn("[onReorder] final failure, silent", err?.response?.status);
        }
      }
    };
    attempt(0);
  };

  const onDeletePhoto = async (photoOrId, opts = {}) => {
    // iter v25 — accetta sia un oggetto photo (chiamato dal tab principale)
    // che un id stringa (chiamato dalla thumbnail con quick action delete).
    // Prima passava `photo.id` ma il backend riceveva undefined → 404.
    // `opts.silent=true` quando in bulk → toast aggregato dal chiamante.
    const photoId = typeof photoOrId === "string" ? photoOrId : photoOrId?.id;
    if (!photoId) {
      if (!opts.silent) toast.error("ID foto mancante");
      throw new Error("missing_photo_id");
    }
    try {
      await api.delete(`/photos/${photoId}`);
      setPhotos((ps) => ps.filter((p) => p.id !== photoId));
      if (activePhotoId === photoId) setActivePhotoId(null);
      if (!opts.silent) toast.success("Foto rimossa");
    } catch (err) {
      console.error("[onDeletePhoto] failed", photoId, err?.response?.status, err);
      if (!opts.silent) toast.error("Errore rimozione");
      throw err;
    }
  };

  const onGenerateDescription = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/properties/${id}/description`, { tone, length });
      setProperty((p) => ({ ...p, ai_description: data.description }));
      toast.success("Descrizione generata");
      setTab("descrizione");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore generazione");
    } finally { setGenerating(false); }
  };

  const onDeleteProperty = () => {
    // iter r95d · B1 · Se questo annuncio ha un batch enhance ancora
    // running, chiedo conferma esplicita prima di aprire il modale
    // "perché elimini?". Root cause del user report: partiva un delete
    // durante "Migliora tutte" e la app si impallava senza feedback.
    // Ora il flow diventa: conferma bloccante → modale ragione → delete
    // ottimistico. Se nessun batch attivo, comportamento identico a
    // prima (nessun prompt extra).
    const activeOnThis = Array.isArray(activeBatches)
      ? activeBatches.filter((b) => b?.property_id === id)
      : [];
    if (activeOnThis.length > 0) {
      const totalDone = activeOnThis.reduce((s, b) => s + (b.done || 0), 0);
      const totalTot = activeOnThis.reduce((s, b) => s + (b.total || 0), 0);
      const remaining = Math.max(0, totalTot - totalDone);
      const msg = remaining > 0
        ? `Ci sono ancora ${remaining} foto in elaborazione su questo annuncio (${totalDone}/${totalTot} pronte).\n\nSe elimini adesso perderai il lavoro in corso e i crediti già consumati.\n\nProcedere con l'eliminazione?`
        : `Le foto di questo annuncio stanno finendo di elaborarsi.\n\nProcedere comunque con l'eliminazione?`;
      // window.confirm è nativo su Capacitor iOS e non richiede un modale
      // custom nuovo. UX standard e non-invasiva.
      const ok = typeof window !== "undefined" && window.confirm ? window.confirm(msg) : true;
      if (!ok) return;
    }
    // iter 244: apriamo il modal "perché elimini?" — popola il Wrapped annuale.
    // L'utente può comunque cliccare "Elimina senza specificare".
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async (payload) => {
    // iter 287 — ottimistico: chiudo subito modal + naviga alla lista annunci.
    // Il delete è cascade (foto, contenuti, social) e su DB grande può durare
    // 2-4s: prima l'overlay nero bloccava tutto e l'utente pensava fosse rotto.
    setDeleteModalOpen(false);
    toast.success(
      payload?.reason === "sold"
        ? "Vendita registrata. Sarà nel tuo Wrapped annuale."
        : "Immobile eliminato",
    );
    // iter v28 r393 — User M1302: Dashboard usa una cache localStorage
    // (`pronto_properties_cache`) per il fast-paint. Senza invalidarla,
    // dopo il delete l'utente arriva su /dashboard/annunci e vede ancora
    // la card del property cancellato (zombie card). Cliccando "Elimina"
    // sulla card zombie il backend ritorna 404 → toast errore confuso.
    // Soluzione: rimuovo subito il property dalla cache prima di navigare.
    try {
      const cached = JSON.parse(localStorage.getItem("pronto_properties_cache") || "[]");
      const filtered = Array.isArray(cached) ? cached.filter((p) => p?.id !== id) : [];
      localStorage.setItem("pronto_properties_cache", JSON.stringify(filtered));
    } catch { /* quota / parse errors are non-blocking */ }
    nav("/dashboard/annunci");
    // Fire-and-forget: il cleanup avviene in background. Se fallisce, segnale al toast.
    try {
      const hasReason = payload && payload.reason;
      if (hasReason) {
        await api.delete(`/properties/${id}`, { data: payload });
      } else {
        await api.delete(`/properties/${id}`);
      }
    } catch {
      toast.error("Errore eliminazione lato server. L'immobile potrebbe riapparire.");
    }
  };

  const onDownloadZip = (kind) => {
    const token = Cookies.get("vista_jwt");
    const qs = new URLSearchParams({ kind });
    if (token) qs.append("auth", token);
    window.open(`${API}/properties/${id}/photos/zip?${qs.toString()}`, "_blank");
  };

  const onExportXml = async () => {
    // iter 313 r19 — bug fix mobile: window.open su iOS Safari mostra
    // una tab bianca (Safari non renderizza XML inline + il header
    // Content-Disposition non triggera download da open()).
    // Fetch autenticato + smartDownloadBlob → share-sheet su iOS,
    // download classico su desktop/Android.
    const token = Cookies.get("vista_jwt");
    const qs = new URLSearchParams();
    if (token) qs.append("auth", token);
    const url = `${API}/properties/${id}/export.xml?${qs.toString()}`;
    try {
      const r = await fetch(url, {
        headers: { "x-vista-origin": window.location.origin, Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const txt = await r.text();
        let detail = "Errore export XML";
        try { detail = JSON.parse(txt)?.detail || detail; } catch { /* not json */ }
        throw new Error(detail);
      }
      const blob = await r.blob();
      const safeTitle = (property?.title || "annuncio")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .slice(0, 30);
      const filename = `pronto-feed-${safeTitle}.xml`;
      await smartDownloadBlob(blob, filename, "application/xml");
      if (isIOSDevice()) {
        toast.success("Feed XML pronto. Tocca 'Condividi' per salvarlo o inviarlo.");
      } else {
        toast.success("Feed XML scaricato.");
      }
    } catch (err) {
      toast.error(err?.message || "Errore export XML");
    }
  };

  const realPhotos = photos.filter((p) => (p.photo_kind || "photo") === "photo");
  const floorPlans = photos.filter((p) => p.photo_kind === "floor_plan");
  // iter v28 r87 — activePhotoId === null è stato esplicito "editor chiuso":
  // l'utente ha cliccato la X. NON fare fallback su realPhotos[0]. Mostra
  // invece l'uploader "Carica foto".
  const activePhoto = activePhotoId === null ? null : (realPhotos.find((p) => p.id === activePhotoId) || realPhotos[0]);
  const activePlan = activePlanId === null ? null : (floorPlans.find((p) => p.id === activePlanId) || floorPlans[0]);
  // Auto-detect plan orientation: rotate only if the source image is taller than wide.
  const [planVertical, setPlanVertical] = useState(false);
  useEffect(() => {
    if (!activePlan?.original_path) { setPlanVertical(false); return; }
    const img = new Image();
    img.onload = () => setPlanVertical(img.naturalHeight > img.naturalWidth);
    img.src = authedFileUrl(activePlan.original_path);
  }, [activePlan?.id, activePlan?.original_path]);

  if (loading) {
    return (
      <AppShell title="Caricamento...">
        {/* iter v28 r302 — Skeleton meno "vuoto": titolo + sub + nav numerata
            + 3 placeholders foto. Comunica già la struttura della pagina
            così l'utente sa cosa sta per arrivare. */}
        <div className="space-y-3">
          <div className="h-7 w-1/2 bg-[#EAE7DF] vista-pulse rounded-md" />
          <div className="h-4 w-1/3 bg-[#EAE7DF]/60 vista-pulse rounded-md" />
        </div>
        <div className="mt-6 flex gap-2 overflow-hidden">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="h-10 w-32 shrink-0 bg-[#EAE7DF] vista-pulse rounded-full" />
          ))}
        </div>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[1, 2, 3].map((n) => (
            <div key={n} className="aspect-[4/3] bg-[#EAE7DF] vista-pulse rounded-lg" />
          ))}
        </div>
        <div className="mt-4 text-[12px] text-[#9A968B] italic text-center">
          {loadError ? "L'annuncio non si è caricato. Prova a riaprirlo." : "Sto aprendo l\u2019annuncio\u2026"}
        </div>
        {/* iter v33 r17 — Sblocco utente se il caricamento resta appeso.
            Dopo 12s appaiono Riprova + torna alla lista, così l'agente
            non è mai bloccato indefinitamente (bug iOS timer in background).
            iter v33 r18 — Se `loadError` è true, mostro i pulsanti SUBITO
            (server ha risposto con errore, non serve aspettare 12s). */}
        {(loadingElapsed >= 12 || loadError) && (
          <div className="mt-6 flex flex-col items-center gap-2" data-testid="loading-stuck-actions">
            <div className="text-[12.5px] text-[#6B6A65] text-center max-w-xs leading-snug">
              {loadError
                ? "C'è stato un intoppo momentaneo. Riprova, di solito basta."
                : "Ci sta mettendo più del solito. Se non si sblocca in pochi secondi, riprova o torna alla lista."}
            </div>
            <div className="flex gap-2 mt-1">
              <button
                type="button"
                onClick={() => { setLoadError(false); load(); }}
                className="inline-flex items-center gap-1.5 bg-[#1C1C1A] hover:bg-black text-white px-4 py-2 rounded-full text-[13px] font-semibold transition-colors"
                data-testid="loading-retry-btn"
              >
                Riprova
              </button>
              <button
                type="button"
                onClick={() => nav("/dashboard/annunci")}
                className="inline-flex items-center gap-1.5 bg-white border border-[#E5E7E0] hover:border-[#7C8B6E] text-[#1C1C1A] px-4 py-2 rounded-full text-[13px] font-semibold transition-colors"
                data-testid="loading-back-btn"
              >
                Torna alla lista
              </button>
            </div>
          </div>
        )}
      </AppShell>
    );
  }

  // iter v33 r26 · Schermata "Sto ancora preparando..." RIMOSSA.
  // L'agente entra sempre direttamente nell'annuncio dopo il sopralluogo:
  // vede le foto originali gia' caricate, e migliora quando vuole lui.
  // Zero gate, zero blocco, zero attesa.
  // Se dovesse restare un vecchio flag localStorage da versioni precedenti,
  // lo pulisco silenziosamente per evitare comportamenti fantasma.
  try {
    if (typeof window !== "undefined") {
      const raw = localStorage.getItem("pronto_in_creazione_v1");
      if (raw) {
        localStorage.removeItem("pronto_in_creazione_v1");
        window.dispatchEvent(new Event("pronto:in-creazione"));
      }
    }
  } catch { /* noop */ }

  // iter 247: aggreghiamo tutto lo stato/handler in un singolo `ctx` esposto
  // via Context ai tab estratti (FotoTab, AutopilotTab, …). Niente prop drilling.
  const ctx = {
    // identifiers / property data
    id, property, setProperty, photos, setPhotos, listing, setListing,
    stats, setStats, estimate, estimating, viewings, leads,
    autopilot, setAutopilot, autopilotLoading, autopilotShowMore, setAutopilotShowMore,
    sellerMode, setSellerMode, voiceRecording, voiceTranscript, setVoiceTranscript, voiceUploading,
    portalLinks, pack, packLoading,
    // photo state
    enhanceMode, setEnhanceMode, enhanceFoci, setEnhanceFoci,
    enhanceFocus, setEnhanceFocus,
    activePhotoId, setActivePhotoId, activePlanId, setActivePlanId,
    batchLoading, batchProgress, enhancingId, stagingId, furnishingId,
    uploading, uploadProgress,
    planStyle, setPlanStyle, planVertical,
    stageStyle, setStageStyle, stageRoom, setStageRoom,
    restyleId, restyleStyle, setRestyleStyle, restyleRoom, setRestyleRoom,
    restyleChangeFloor, setRestyleChangeFloor, restyleFloorStyle, setRestyleFloorStyle,
    showRestyle, setShowRestyle, showStage, setShowStage, styleConsistency,
    // iter v28 r390 — Svuota stanza
    emptyRoomId, emptyRoomHint, setEmptyRoomHint,
    // computed
    realPhotos, floorPlans, activePhoto, activePlan,
    // generation / annuncio
    generating, tone, setTone, length, setLength,
    // navigation
    tab, setTab, nav,
    // modals/state
    publishOpen, setPublishOpen,
    editOpen, setEditOpen, deleteModalOpen, setDeleteModalOpen,
    matchModalOpen, setMatchModalOpen, matchModalShareLink,
    shareOpen, setShareOpen, shareLink, shareLinkHuman, linkCopied,
    // refs
    fileInputRef, planInputRef,
    // handlers
    load, onUpload, onUploadPlan, onFilePick, onPlanFilePick, onDeletePhoto, onReorder, onDownloadZip, onExportXml,
    // iter v33 r5 — User M1478: «non usa AI, almeno non si impalla». Tutte
    // le azioni AI pesanti (Nano Banana / LLM) vengono avvolte da
    // `guardAIAction`: se c'è un sopralluogo in lavorazione (flag
    // `pronto_in_creazione_v1`), un toast informa l'agente "AI in pausa
    // · sto preparando il tuo sopralluogo" e blocca l'esecuzione. In
    // pratica: i bottoni Migliora/Arreda/Svuota stanza/Cambia arredo/
    // Genera descrizione/Migliora tutte funzionano come prima quando
    // niente è in coda, ma sono protetti quando lo è.
    onEnhance: guardAIAction(onEnhance, "Migliora foto"),
    onStage: guardAIAction(onStage, "Arreda stanza"),
    onRestyle: guardAIAction(onRestyle, "Cambia arredo"),
    onEmptyRoom: guardAIAction(onEmptyRoom, "Svuota stanza"),
    onBatchEnhance: guardAIAction(onBatchEnhance, "Migliora tutte le foto"),
    onCancelBatch, cancellingBatch, activeBatchJobId,
    onFurnishPlan: guardAIAction(onFurnishPlan, "Arreda la pianta"),
    onGenerateDescription: guardAIAction(onGenerateDescription, "Genera descrizione"),
    onGeneratePortalPack: guardAIAction(onGeneratePortalPack, "Prepara pacchetto portale"),
    currentFocusPayload,
    onRunAutopilot, onStartRecording, onStopRecording,
    onUpdateViewing,
    onActivateListing, onToggleListing, onDeleteListing, onEstimate, copyPublicLink, downloadQr, downloadQrWa,
    onOpenShare, copyShareLink, shareWhatsapp, shareEmail, shareNative,
    publicListingUrl, publicShareUrl,
    onDeleteProperty, handleDeleteConfirm,
  };

  return (
    <PropertyDetailCtx.Provider value={ctx}>
    <AppShell title={property?.title || "Immobile"} breadcrumb={property?.title || "Immobile"}>
      {/* iter v33 r52 · Overlay bloccante "Sto preparando l'annuncio" per chi
          arriva da taccuino: attende che tutte le foto siano visibili nella
          galleria prima di lasciarlo entrare. Vedi TaccuinoWaitingOverlay.jsx. */}
      {waitingForPhotos && (
        <TaccuinoWaitingOverlay
          photos={photos}
          expectedCount={property?.expected_photo_count || null}
          onDismiss={() => {
            setWaitingForPhotos(false);
            // Rimuovo il param `from=taccuino` così l'utente non re-innesca
            // l'overlay se ricarica la pagina.
            try {
              const next = new URLSearchParams(searchParams);
              next.delete("from");
              setSearchParams(next, { replace: true });
            } catch { /* noop */ }
          }}
        />
      )}
      {/* iter 304: back button intelligente — ricorda da dove vieni.
          Quando entri in un annuncio salviamo in sessionStorage l'URL della
          pagina precedente; il tasto "Chiudi" torna lì invece di andare
          sempre a /dashboard/annunci. Default = "Tutti gli immobili". */}
      {(() => {
        let back = { url: "/dashboard/annunci", label: "Tutti gli immobili" };
        try {
          const stored = sessionStorage.getItem(`pronto_back_for_${id}`);
          if (stored) back = JSON.parse(stored);
        } catch { /* noop */ }
        return (
          <div className="flex items-center justify-between gap-3 mb-5">
            <button
              onClick={() => nav(back.url)}
              className="text-sm text-[#6B7280] hover:text-[#7C8B6E] flex items-center gap-2"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft size={14} /> {back.label}
            </button>
            <button
              onClick={() => nav(back.url)}
              className="text-[12px] text-[#9A968B] hover:text-[#1C1C1A] inline-flex items-center gap-1 px-2.5 py-1 rounded-full hover:bg-[#F1F4ED] transition-colors"
              data-testid="close-property-shortcut"
              title={`Chiudi · torna a ${back.label}`}
            >
              <span className="text-[15px] leading-none">×</span> Chiudi
            </button>
          </div>
        );
      })()}

        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <span className="overline">{property?.property_type}</span>
            {/* iter v28 r211 — User M1131: header property title in Manrope sans
                (era font-editorial EB Garamond Italic, troppo "decorativo" nella
                vista interna dell'agente). La classe font-editorial resta su EB
                Garamond per la landing hero e altre sezioni magazine-style.
                iter v29 r245 — User M1399: ottimizzazione mobile. Title era
                text-4xl (36px) anche su iPhone — troppo grande, prendeva 2 righe
                spesso 3 per titoli lunghi. Ora: text-2xl mobile (24px), text-4xl
                sm+ (640px+), text-5xl desktop. */}
            <h1 className="text-2xl sm:text-4xl lg:text-5xl tracking-tight mt-2 font-semibold text-[#111827] leading-tight" data-testid="property-title">
              {property?.title}
            </h1>
            <div className="mt-2 text-[#6B6A65] text-[12.5px] sm:text-sm flex flex-wrap items-center gap-x-3 gap-y-0.5">
              <span data-testid="property-price">{formatPrice(property?.price)}</span>
              {property?.surface && <span>· {property.surface} m²</span>}
              {property?.rooms && <span>· {property.rooms} locali</span>}
              {property?.bathrooms && <span>· {property.bathrooms} bagni</span>}
              {property?.city && <span>· {property.city}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* iter 271 round 2: badge "Contatti ricevuti" — counter live
                con dot rosso pulsante se ci sono richieste non gestite.
                Click porta direttamente al sub-tab lead in Promuovi. */}
            {(() => {
              const newCount = viewings.filter((v) => v.status === "pending" || !v.status).length;
              if (newCount === 0) return null;
              return (
                <button
                  onClick={() => {
                    setTab("condivisione");
                    setTimeout(() => {
                      // scrolla al LeadTab dentro la scheda Promuovi (è in fondo)
                      const el = document.querySelector('[data-testid="leads-section"]') || document.querySelector('[data-testid="tab-lead"]');
                      el?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 250);
                  }}
                  className="relative px-3 py-2 text-[12.5px] font-semibold bg-[#FFF5E5] text-[#8C5A00] border border-[#F0D9A8] rounded-full hover:bg-[#FFEFD2] transition-colors inline-flex items-center gap-1.5 animate-pulse"
                  data-testid="leads-badge-header"
                  title={`${newCount} richieste in attesa di risposta`}
                  style={{ animationDuration: "2.5s" }}
                >
                  <span className="absolute -top-0.5 -left-0.5 w-2 h-2 rounded-full bg-[#C66060] ring-2 ring-white" />
                  <Users size={13} weight="fill" />
                  {newCount} {newCount === 1 ? "richiesta" : "richieste"}
                </button>
              );
            })()}
            {/* iter v28 r92 — Quick-share visibile entrando nell'annuncio
                (richiesta utente: "appena uno entra nell'annuncio, mettere
                che posso inviare il link di condivisione"). Solo se è già
                online; altrimenti porta al tab Pubblica. */}
            {listing?.exists && listing?.is_public ? (
              <button
                onClick={onOpenShare}
                className="px-3 py-2 text-[12.5px] font-semibold bg-[#7C8B6E] text-white rounded-full hover:bg-[#6B7A5E] transition-colors inline-flex items-center gap-1.5 shadow-sm"
                data-testid="header-share-link-btn"
                title="Condividi il link della vetrina personale (WhatsApp, email, copia)"
              >
                <ShareNetwork size={13} weight="bold" /> Condividi link
              </button>
            ) : (
              <button
                onClick={() => setTab("condivisione")}
                className="px-3 py-2 text-[12.5px] font-semibold bg-white text-[#7C8B6E] border border-[#7C8B6E] rounded-full hover:bg-[#F1F4ED] transition-colors inline-flex items-center gap-1.5"
                data-testid="header-publish-link-btn"
                title="Pubblica e ottieni il link condivisibile"
              >
                <ShareNetwork size={13} weight="bold" /> Pubblica & condividi
              </button>
            )}
            {/* iter 261: pulsante "Modifica dati" — l'utente non sapeva come
                cambiare titolo/mq/piano/prezzo dopo la creazione. Apriamo
                un modal compatto con tutti i campi editoriali. */}
            <button
              onClick={() => setEditOpen(true)}
              className="px-3 py-2 text-[12.5px] font-medium text-[#1C1C1A] border border-[#E5E7E0] rounded-full hover:border-[#7C8B6E] hover:bg-[#F1F4ED] transition-colors inline-flex items-center gap-1.5"
              data-testid="edit-property-btn"
              title="Modifica titolo, prezzo, metri, piano…"
            >
              <Sliders size={14} weight="bold" /> Modifica dati
            </button>
            <button
              onClick={onDeleteProperty}
              className="p-2.5 text-[#6B6A65] hover:text-[#B8624A] hover:bg-[#F1F4ED] transition-colors rounded-sm"
              title="Elimina immobile"
              data-testid="delete-property-btn"
            >
              <Trash size={18} weight="regular" />
            </button>
          </div>
        </div>

        {/* Workflow hint — "cosa fare adesso" */}
        {(() => {
          const realCount = photos.filter((p) => (p.photo_kind || "photo") === "photo").length;
          const enhancedCount = photos.filter((p) => (p.photo_kind || "photo") === "photo" && p.enhanced_path).length;
          const hasListing = !!(property?.autopilot || property?.ai_description || property?.description);
          const isPublic = !!listing?.exists;
          // iter v28 r21 — Ordine wizard rivisto per richiesta utente:
          //   1. Carica (era 3) - foto/planimetria prima di tutto
          //   2. Testo (era 2)
          //   3. Pubblica (era 4)
          //   4. Condividi (era 5)
          // Rimosso il vecchio step "Racconta" perché ridondante: l'utente
          // ha già creato l'annuncio (era step pre-creazione).
          let step = null;
          if (realCount === 0) step = { label: "1. Carica · carica le foto dell'immobile", cta: "Carica foto", tabKey: "foto" };
          else if (enhancedCount < realCount) step = { label: `1. Carica · migliora ancora ${realCount - enhancedCount} foto`, cta: "Migliora foto", tabKey: "foto" };
          else if (!hasListing) step = { label: "2. Testo · rivedi titolo e dati dell'annuncio", cta: "Vai al Testo", tabKey: "descrizione" };
          else if (!isPublic) step = { label: "3. Pubblica · rendi online il tuo annuncio", cta: "Vai a Pubblica", tabKey: "condivisione" };
          else step = { label: "✓ Tutto pronto · l'annuncio è online sui canali Pronto", cta: "Vedi performance", tabKey: "performance" };
          return (
            <div
              className="mt-8 flex items-center justify-between gap-4 flex-wrap px-5 py-3 bg-[#F1F4ED] border-l-4 border-[#7C8B6E]"
              data-testid="workflow-hint"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#7C8B6E] font-semibold shrink-0">Prossimo passo</span>
                <span className="text-sm text-[#1C1C1A] truncate" data-testid="workflow-hint-label">{step.label}</span>
              </div>
              {step.tabKey !== tab && (
                <button
                  onClick={() => setTab(step.tabKey)}
                  className="text-sm text-[#7C8B6E] font-medium underline underline-offset-4 hover:opacity-80 shrink-0"
                  data-testid="workflow-hint-cta"
                >
                  {step.cta} →
                </button>
              )}
            </div>
          );
        })()}

        {/* iter v28 r21 — Stepper rivisto (era 5 step, ora 4):
            1. Carica (foto/pianta/docs) — era 3
            2. Testo (descrizione/anteprima/controllo qualità) — era 2
            3. Pubblica (portali + pagina online) — era 4
            4. Condividi (social + materiali) — era 5
            Rimosso step "Racconta" (1 originale) perché ridondante dopo
            la creazione dell'annuncio: l'utente è già qui, ha già parlato.
            Il tab "dimmi" resta accessibile dal sub-tab "Carica" se serve
            ri-dettare/ricaricare via voce nuovo testo. */}
        {(() => {
          const enhancedCount = realPhotos.filter((p) => p.enhanced_path).length;
          const hasContenuti = realPhotos.length > 0 || floorPlans.length > 0;
          const hasAnnuncio = !!property?.ai_description;
          const hasFile = enhancedCount > 0 || !!property?.ai_description || !!floorPlans[0]?.furnished_plan_path;
          const hasPromuovi = !!listing?.exists;
          const sections = [
            // iter v33 r9 · User M1486: «forse il social è una cosa in
            // più per dopo che l'hai pubblicato». Riorganizzo i 4 step
            // seguendo il flusso mentale reale: prepari (foto+testo) →
            // pubblichi (go live) → promuovi (amplifica). Rinomino
            // "Vetrina e portali" in "Pubblica" per rendere ovvio che
            // è il punto di GO-LIVE dell'annuncio.
            { id: "contenuti", num: "1", label: "Prepara i materiali", sub: "Foto, planimetria catastale e documenti (APE) — la base dell'annuncio.", Icon: ImageIcon, tabs: ["foto", "pianta", "documenti"], done: hasContenuti },
            { id: "annuncio",  num: "2", label: "Scrivi l'annuncio",   sub: "Descrizione, analisi e anteprima. Pronto rifinisce il testo e te lo mostra come apparirà.", Icon: Sparkle, tabs: ["descrizione", "autopilot", "anteprima"], star: true, done: hasAnnuncio },
            { id: "marketing", num: "3", label: "Pubblica",            sub: "Il momento del go-live: attivi la Vetrina personale (il tuo sito) e pubblichi sui portali (Immobiliare, Idealista, ecc.).", Icon: Rocket, tabs: ["condivisione", "portali"], done: hasPromuovi },
            { id: "condividi", num: "4", label: "Promuovi",            sub: "Dopo la pubblicazione: post per Instagram, materiali pronti da inviare al proprietario o ai clienti interessati.", Icon: Share, tabs: ["social", "file"], done: hasFile && hasPromuovi },
          ];
          const subLabels = {
            dimmi: "Inizia qui",
            foto: "Foto", pianta: "Planimetria", documenti: "Documenti",
            // iter v28 r95 — Rebrand: "Testo" → "Descrizione" (richiesta utente).
            // Ordine interno della sezione: Descrizione → Analizza → Anteprima.
            descrizione: "Descrizione", autopilot: "Analizza l'annuncio", anteprima: "Anteprima",
            // iter v33 r9 — "Vetrina personale" → "Sul tuo sito" (più diretto).
            // "Portali" resta breve. "Editor social" → "Post per social" (più
            // immediato per un agente).
            condivisione: "Sul tuo sito", portali: "Sui portali", social: "Post per social", file: "Materiali",
          };
          const activeSection = sections.find((s) => s.tabs.includes(tab)) || sections[0];
          return (
            <>
              {/* iter v29 r087 — Progress mentale: "STEP X di 4" + nome step.
                  L'agente vede subito dove sta nel flusso e cosa resta. */}
              {(() => {
                const totalSteps = sections.length;
                const currentIdx = sections.findIndex((s) => s.id === activeSection.id) + 1;
                const doneCount = sections.filter((s) => s.done).length;
                const pct = Math.round((doneCount / totalSteps) * 100);
                return (
                  <div className="mt-5 mb-1 flex items-center gap-3 px-1" data-testid="section-progress">
                    <div className="flex-shrink-0 text-[10px] font-bold tracking-[0.22em] uppercase text-[#7C8B6E]">
                      STEP {currentIdx} / {totalSteps}
                    </div>
                    <div className="flex-1 h-1 bg-[#E5E7E0] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#7C8B6E] transition-all duration-500 ease-out"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="flex-shrink-0 text-[11px] font-medium text-[#9A968B] tabular-nums">
                      {doneCount}/{totalSteps} fatto
                    </div>
                  </div>
                );
              })()}

              {/* Section pills · numerate.
                  iter v29 r124 · User M1364: il pallino "novità" -top-0.5 era
                  fuori dal contenitore overflow-hidden → tagliato. Aggiungo
                  padding verticale al contenitore così il ring resta visibile. */}
              <div className="mt-3 -mx-1 px-1 py-1.5 flex items-center gap-2 overflow-x-auto hide-scroll" data-testid="section-nav">
                {sections.map(({ id, num, label, Icon, tabs, star, done }) => {
                  const active = activeSection.id === id;
                  // iter v28 r299 — Sezione "ha novità" se almeno un suo tab
                  // ha il badge nuovo.
                  const sectionHasNew = !active && tabs.some((t) => tabBadges[t]);
                  return (
                    <button
                      key={id}
                      onClick={() => setTab(tabs[0])}
                      className={`px-4 py-2.5 text-sm font-medium rounded-full inline-flex items-center gap-2 whitespace-nowrap transition-all relative ${
                        active
                          ? "bg-[#1C1C1A] text-white shadow-sm"
                          : "bg-[#F1F4ED] text-[#1C1C1A] hover:bg-[#E5E7E0]"
                      }`}
                      data-testid={`section-${id}`}
                    >
                      <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[11px] font-bold ${
                        active ? "bg-white/15 text-white" : "bg-[#1C1C1A]/10 text-[#1C1C1A]"
                      }`}>{num}</span>
                      <Icon size={14} weight={star ? "fill" : "regular"} /> {label}
                      {done && (
                        <span
                          className={`shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full ${
                            active ? "bg-white/15 text-[#A8B89A]" : "bg-[#7C8B6E]/15 text-[#7C8B6E]"
                          }`}
                          aria-label="Completato"
                          data-testid={`section-${id}-done`}
                        >
                          <Check size={9} weight="bold" />
                        </span>
                      )}
                      {star && !done && <span className={active ? "text-[#A8B89A] text-xs" : "text-[#7C8B6E] text-xs"}>★</span>}
                      {sectionHasNew && (
                        <span
                          className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#7C8B6E] ring-2 ring-white animate-pulse"
                          aria-label="Aggiornamenti recenti"
                          data-testid={`section-${id}-badge-new`}
                          title="Aggiornamenti recenti"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
              {/* Descrizione della sezione attiva — spiega a cosa serve.
                  iter v28 r298 — Pulsante "?" centralizzato a destra: in base al
                  tab attivo riapre le guide DismissibleIntro corrispondenti.
                  Mappa tab → storageKeys mantenuta in `TAB_GUIDE_KEYS` qui sotto. */}
              <div className="mt-3 flex items-start justify-between gap-3">
                <p className="text-[12.5px] text-[#6B6A65] italic flex-1" data-testid="section-description">
                  {activeSection.sub}
                </p>
                {(() => {
                  const TAB_GUIDE_KEYS = {
                    foto: ["foto:intro"],
                    pianta: ["propertydetail:pianta-intro"],
                    descrizione: ["annuncio:3-passi"],
                    anteprima: ["anteprima:intro"],
                    documenti: ["propertydetail:documenti-intro"],
                    file: ["propertydetail:file-intro"],
                    social: ["social:editor-intro"],
                    portali: ["portali:intro", "portali:pubblica-guidata-intro"],
                  };
                  const keys = TAB_GUIDE_KEYS[tab];
                  return keys && keys.length > 0 ? (
                    <ReopenGuidesBtn keys={keys} testId={`tab-${tab}-reopen-guides`} />
                  ) : null;
                })()}
              </div>
              {/* Sub-tabs della sezione attiva (solo se ne ha più di una) */}
              {activeSection.tabs.length > 1 && (
                <div className="mt-3 flex items-center gap-1 border-b border-[#E5E7E0] overflow-x-auto hide-scroll" data-testid="subtab-nav">
                  {activeSection.tabs.map((k) => {
                    const hasNew = !!tabBadges[k];
                    return (
                      <button
                        key={k}
                        onClick={() => setTab(k)}
                        className={`px-3 py-2 text-[13px] font-medium transition-colors border-b-2 -mb-px whitespace-nowrap inline-flex items-center gap-1.5 ${
                          tab === k ? "border-[#7C8B6E] text-[#1C1C1A]" : "border-transparent text-[#6B6A65] hover:text-[#1C1C1A]"
                        }`}
                        data-testid={`tab-${k}`}
                      >
                        {subLabels[k]}
                        {hasNew && (
                          <span
                            className="inline-block w-1.5 h-1.5 rounded-full bg-[#7C8B6E] animate-pulse"
                            aria-label="Aggiornamenti recenti"
                            data-testid={`tab-${k}-badge-new`}
                            title="Aggiornamenti recenti"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          );
        })()}

        {/* iter v28 r307 — Banner Kit in progress
            Mostra in alto i contenuti che Pronto sta preparando dopo
            "Crea annuncio" con stato in tempo reale. Risolve la
            confusione «dove sono i contenuti che ho selezionato?».
            iter v28 r380 — User M1290: il banner mandava il click a
            `setPhotoReviewOpen(true)` ma l'utente non capiva "cosa rivedere"
            (le foto sono già migliorate in automatico dal sopralluogo).
            Tolto il click sulla riga "Rivedi le foto" — adesso il banner
            mostra solo stato/progress, niente azione di review.

            iter v32 r31 — User M1414: «se clicco non mi porta alla
            sezione». Mappa job.kind → tab destinazione e naviga subito.
            Le righe in lavorazione restano statiche; le ready/awaiting
            diventano cliccabili. */}
        <KitProgressBanner
          propertyId={id}
          onReviewClick={(job) => {
            // iter v33 r7 — User M1484: «Rivedi non mi va in quella sezione».
            // I job in awaiting_review (reel/before_after/social_post) sono
            // in pausa perché aspettano che l'agente approvi le foto. Devo
            // portarlo al tab Foto con ?review=1 (che apre il modal di
            // revisione foto), non al tab File del job.
            setTab("foto");
            setSearchParams((prev) => {
              const np = new URLSearchParams(prev);
              np.set("tab", "foto");
              np.set("review", "1");
              return np;
            }, { replace: true });
            try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* */ }
          }}
          onJobClick={(job) => {
            const k = job?.kind;
            // iter v33 r7 — Se il job enhance_photos è pending (timeout parziale
            // o retry necessari), porto direttamente nel tab Foto — l'agente
            // vede "N foto da migliorare · Migliora tutte" e riprova.
            if (k === "enhance_photos") {
              setTab("foto");
              try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* */ }
              return;
            }
            // Mapping kind → tab di destinazione
            const KIND_TO_TAB = {
              social_post:       "file",
              reel:              "file",
              before_after:      "file",
              vetrina_link:      "anteprima",
              email_proprietario:"file",
              crm_card:          "file",
            };
            const target = KIND_TO_TAB[k] || "file";
            setTab(target);
            // Scroll in alto per inquadrare bene il tab appena aperto
            try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* */ }
          }}
        />

        {/* iter v28 r306/r380 — Banner Review foto: RIMOSSO.
            User M1290: «non mi è chiaro il "rivedi foto" cosa serva, nel
            senso che io una volta che ho fatto il sopralluogo e generato
            l'annuncio, lui mi carica la foto e me la fa già migliorata
            in automatico». Le foto sono già nel tab Foto migliorate; non
            serve un banner separato per "rivederle". Se l'utente vuole
            controllarle, può andare direttamente nel tab Foto. Il modal
            PhotoReviewModal resta nel codice per uso da menu o link
            diretto futuri. */}

        {/* FOTO TAB */}
        {/* FOTO TAB — extracted to ./propertyDetail/tabs/FotoTab.jsx */}
        {/* iter v33 r17 · Wrap in ErrorBoundary con fallback minimo: se
            un rendering della foto attiva crasha (dati corrotti, URL malformato),
            l'agente vede un messaggio chiaro con Riprova invece di schermo bianco. */}
        {tab === "foto" && (
          <ErrorBoundary
            silent
            fallback={
              <div className="card-flat p-6 text-center" data-testid="foto-tab-error">
                <div className="font-editorial text-xl text-[#1C1C1A] mb-2">Foto momentaneamente non visualizzabili</div>
                <p className="text-[13px] text-[#6B6A65] mb-4 leading-relaxed">
                  Qualcosa in questo annuncio ha impedito di mostrare la galleria. Ricarica la pagina, di solito basta.
                </p>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="btn-primary"
                  data-testid="foto-tab-error-reload"
                >
                  Ricarica la pagina
                </button>
              </div>
            }
          >
            <FotoTab />
          </ErrorBoundary>
        )}
        {/* AUTOPILOT TAB — extracted to ./propertyDetail/tabs/AutopilotTab.jsx */}
        {tab === "autopilot" && <AutopilotTab />}

        {/* PIANTA TAB — pianta catastale → pianta arredata */}
        {tab === "pianta" && <PiantaTab />}
        {tab === "descrizione" && <DescrizioneTab />}
        {tab === "anteprima" && <AnteprimaTab />}
        {tab === "documenti" && <DocumentiTab />}
        {tab === "file" && <MaterialiTab />}
        {tab === "dimmi" && <DimmiTab />}

        {/* iter v28 r174 — User M1091: «In pubblica portali subito mi esce
            zero clienti compatibili... mettimela in fondo a quella pagina lì,
            sempre da mobile». MatchPanel resta SOPRA al PortaliTab su desktop
            (richiesta storica r60) ma su mobile scivola in fondo via flex
            order. Uso `flex flex-col` come wrapper e ordino i figli. */}
        {tab === "portali" && (
          <div className="flex flex-col">
            <div className="order-2 lg:order-1">
              <MatchPanel
                propertyId={id}
                onOpenSequence={() => {
                  const link = listing?.public_id ? `${window.location.origin}/listing/${listing.public_id}` : "";
                  setMatchModalShareLink(link);
                  setMatchModalOpen(true);
                }}
              />
            </div>
            <div className="order-1 lg:order-2">
              <PortaliTab />
            </div>
          </div>
        )}

        {/* SOCIAL STUDIO TAB */}
        {tab === "social" && (
          <SocialStudioTab propertyId={id} />
        )}

        {/* CONDIVISIONE TAB — iter v28 r94: ordine ottimizzato:
            1. CondivisioneTab (editor vetrina online + link condivisione)
            2. MatchPanel (clienti compatibili)
            3. Lead
            (Rimosso PerformanceTab — l'utente non vuole più i numeri manuali
            da altri portali in questa vista) */}
        {tab === "condivisione" && (
          <>
            <CondivisioneTab />
            <div className="my-12 border-t border-[#E5E7E0]" />
            <MatchPanel
              propertyId={id}
              onOpenSequence={() => {
                const link = listing?.public_id ? `${window.location.origin}/listing/${listing.public_id}` : "";
                setMatchModalShareLink(link);
                setMatchModalOpen(true);
              }}
            />
            <div className="my-12 border-t border-[#E5E7E0]" />
            <LeadTab />
          </>
        )}

      {/* Publish Now dialog (Autopilot CTA) */}
      <PublishNowDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        property={property}
        propertyId={id}
        photos={photos}
        pack={pack}
        packLoading={packLoading}
        onGeneratePack={onGeneratePortalPack}
      />

      {/* Publish-match modal — opens automatically right after a NEW listing is created */}
      <PublishMatchModal
        open={matchModalOpen}
        onClose={() => setMatchModalOpen(false)}
        propertyId={id}
        propertyTitle={property?.title}
        shareLink={matchModalShareLink}
      />

      {/* Share with client modal */}
      {shareOpen && (
        <div
          className="fixed inset-0 z-50 bg-[#1C1C1A]/60 flex items-center justify-center p-4"
          onClick={() => setShareOpen(false)}
          data-testid="share-modal-overlay"
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-7 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="share-modal"
          >
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="font-editorial text-2xl tracking-tight text-[#1C1C1A]">Condividi col cliente</h3>
                <p className="text-sm text-[#6B6A65] mt-1">
                  Un link unico con foto migliorate, pianta arredata e brochure.
                </p>
              </div>
              <button
                onClick={() => setShareOpen(false)}
                className="text-[#6B6A65] hover:text-[#1C1C1A] -m-2 p-2"
                data-testid="share-close-btn"
                aria-label="Chiudi"
              >✕</button>
            </div>

            {/* WhatsApp-style OG preview — shows the agent EXACTLY how the link will look in chat */}
            {(() => {
              const realPhotos = photos.filter((p) => (p.photo_kind || "photo") === "photo");
              const heroPhoto = realPhotos.find((p) => p.enhanced_path) || realPhotos.find((p) => p.staged_path) || realPhotos[0];
              const heroSrc = heroPhoto ? authedFileUrl(heroPhoto.enhanced_path || heroPhoto.staged_path || heroPhoto.original_path) : null;
              const titleBits = [property?.property_type, property?.title].filter(Boolean).join(" · ");
              const descBits = [
                property?.price ? formatPrice(property.price) : null,
                property?.surface ? `${property.surface} m²` : null,
                property?.rooms ? `${property.rooms} locali` : null,
                property?.city,
              ].filter(Boolean).join(" · ");
              let host = "";
              try { host = new URL(shareLink).host; } catch { host = "pronto"; }
              return (
                <div className="mb-5" data-testid="share-og-preview">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A65] font-medium mb-2">
                    Anteprima su WhatsApp
                  </div>
                  <div className="bg-[#DCF8C6] rounded-xl rounded-tr-sm p-2 max-w-[88%] ml-auto shadow-sm">
                    <div className="bg-white rounded-lg overflow-hidden border border-black/5">
                      {heroSrc ? (
                        <img
                          src={heroSrc}
                          alt=""
                          className="w-full aspect-[1.91/1] object-cover"
                          data-testid="og-preview-image"
                        />
                      ) : (
                        <div className="w-full aspect-[1.91/1] bg-[#F1F4ED] flex items-center justify-center text-[#7C8B6E]">
                          <ImageIcon size={28} weight="light" />
                        </div>
                      )}
                      <div className="px-3 py-2">
                        <div className="text-[12px] font-semibold text-[#1C1C1A] line-clamp-1" data-testid="og-preview-title">
                          {titleBits || "Immobile"}
                        </div>
                        <div className="text-[11px] text-[#6B6A65] line-clamp-2 mt-0.5" data-testid="og-preview-desc">
                          {descBits || "Annuncio immobiliare"}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-[#6B6A65]/80 mt-1.5">
                          {host}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="bg-[#F1F4ED] rounded-xl p-3.5 mb-5 flex items-center gap-2">
              <code className="text-xs text-[#1C1C1A] flex-1 truncate font-mono" data-testid="share-link-preview">{shareLink}</code>
              <button
                onClick={copyShareLink}
                className="text-xs px-2.5 py-1.5 bg-white border border-[#E5E7E0] rounded-lg hover:border-[#7C8B6E] flex items-center gap-1"
                data-testid="share-copy-btn"
              >
                <Copy size={12} /> Copia
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2.5">
              <button
                onClick={shareWhatsapp}
                className="bg-[#25D366] text-white py-3 px-2 rounded-xl text-xs font-medium flex flex-col items-center justify-center gap-1.5 hover:opacity-90"
                data-testid="share-whatsapp-btn"
              >
                <span className="text-xl">💬</span>
                WhatsApp
              </button>
              <button
                onClick={shareEmail}
                className="bg-[#1C1C1A] text-white py-3 px-2 rounded-xl text-xs font-medium flex flex-col items-center justify-center gap-1.5 hover:opacity-90"
                data-testid="share-email-btn"
              >
                <span className="text-xl">✉️</span>
                Email
              </button>
              <button
                onClick={shareNative}
                className="bg-[#F1F4ED] text-[#1C1C1A] py-3 px-2 rounded-xl text-xs font-medium flex flex-col items-center justify-center gap-1.5 hover:bg-[#E5E7E0]"
                data-testid="share-native-btn"
              >
                <ShareNetwork size={20} />
                Altro
              </button>
            </div>

            <div className="mt-5 pt-5 border-t border-[#E5E7E0] space-y-2 text-sm">
              <a
                href={shareLinkHuman || shareLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full text-left flex items-center gap-3 py-2 px-3 -mx-3 rounded-lg hover:bg-[#F1F4ED]"
                data-testid="share-open-public-btn"
              >
                <Eye size={16} className="text-[#7C8B6E]" />
                <span className="flex-1">Apri come lo vede il cliente</span>
                <span className="text-xs text-[#6B6A65]">→</span>
              </a>
            </div>
          </div>
        </div>
      )}

      <PropertyEditModal
        open={editOpen}
        property={property}
        onClose={() => setEditOpen(false)}
        onSaved={(updated) => setProperty(updated)}
      />

      {/* iter v28 r306 — Modal revisione foto del kit post-sopralluogo */}
      <PhotoReviewModal
        propertyId={id}
        open={photoReviewOpen}
        onClose={() => setPhotoReviewOpen(false)}
        onApproved={() => {
          setPhotoReviewStatus("approved");
          // Refresh foto + property dopo approvazione
          load();
        }}
      />

      <DeletePropertyReasonModal
        open={deleteModalOpen}
        title={property?.title}
        askingPrice={property?.price}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      {/* iter v28 r19 — AlertDialog per «Elimina pagina pubblica».
          Sostituisce window.confirm() che era silently-blocked su iOS Safari/PWA. */}
      <AlertDialog open={deleteListingOpen} onOpenChange={setDeleteListingOpen}>
        <AlertDialogContent data-testid="delete-listing-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la pagina pubblica?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-[14px] text-[#1C1C1A]">
                <div className="flex items-start gap-2"><span className="text-[#7C8B6E]">•</span> Il link smette di funzionare subito.</div>
                <div className="flex items-start gap-2"><span className="text-[#7C8B6E]">•</span> Visite e lead collegati vengono cancellati.</div>
                <div className="flex items-start gap-2"><span className="text-[#7C8B6E]">•</span> L&apos;immobile e le foto restano: potrai ripubblicarlo quando vuoi.</div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteListingBusy} data-testid="delete-listing-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDeleteListing(); }}
              disabled={deleteListingBusy}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-listing-confirm"
            >
              {deleteListingBusy ? "Eliminazione…" : "Sì, elimina"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* iter v29 r085 — Voice Command Center: modifica + foto + materiali */}
      <PropertyVoiceFab property={property} photos={photos} onUpdated={load} />

      {/* iter v33 r30 · Gate crediti quando l'agente prova "Migliora tutte"
          e non ne ha abbastanza. Offre acquisto top-up o proseguimento
          parziale con quelli che ha. */}
      <LowCreditsModal
        open={!!lowCreditsInfo}
        balance={lowCreditsInfo?.balance || 0}
        required={lowCreditsInfo?.required || 0}
        costPerPhoto={lowCreditsInfo?.costPerPhoto || 1}
        photoCount={lowCreditsInfo?.photoCount || 0}
        onClose={() => {
          setLowCreditsInfo(null);
          setPendingBatchLaunch(null);
        }}
        onProceedPartial={(affordable) => {
          if (!pendingBatchLaunch) return;
          const partial = pendingBatchLaunch.pending.slice(0, affordable);
          setLowCreditsInfo(null);
          setPendingBatchLaunch(null);
          _runBatchAsync(partial, pendingBatchLaunch.opts, pendingBatchLaunch.modeLabel);
        }}
      />

      {/* iter v33 r35 · User: «fai in modo che io non possa entrare nell'annuncio
          finché non è tutto finito». Overlay bloccante durante Migliora tutte:
          copre l'annuncio, mostra progresso in tempo reale (done/total +
          in_progress) e permette solo Annulla o "torna alla lista in bg". */}
      {batchLoading && batchProgress && (
        <BatchProgressOverlay
          progress={batchProgress}
          onCancel={onCancelBatch}
          onLeaveInBackground={() => nav("/dashboard/annunci")}
          cancelling={cancellingBatch}
          propertyTitle={property?.title || property?.address || "questo annuncio"}
        />
      )}
    </AppShell>
    </PropertyDetailCtx.Provider>
  );
}

